import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Paper, Box, Grid, Typography, IconButton, Button, Divider,
  TextField, Autocomplete, Tooltip, Chip, Alert, Card, CardContent,
  Fade, Zoom, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, FormControl, InputLabel, Select,
  MenuItem, CircularProgress, Stack, FormControlLabel, Checkbox, FormHelperText
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import LockIcon from "@mui/icons-material/Lock";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ScienceIcon from "@mui/icons-material/Science";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import TopicIcon from "@mui/icons-material/Topic";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Edit, Delete, Close, Medication, CameraAlt, Info } from "@mui/icons-material";
import axios from "axios";
import Swal from "sweetalert2";
import { ErrorMessage, successMessage, frequencyMap, frequencyMultiplier, standardFrequencies, MEDICATION_ROUTES } from "../Message";
import { formatWithOutSom } from "../Message";
import { isAdultFromDobOrAge } from "../../utils/age";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import {
  saveAdmissionInfoRound, loadWardRoundsdb,
  loadLabdb, requestLab, loadRequestedLabsInpatients, deleteRequestedLabdb,
  loadAvailableScansdb, loadRequestedScanInpatient,
  loadMedicationsdb, addMedicationdb, loadRequestedMedsInpatient, updateMedicationdb, medicationdeletedb,
  loadOtherServicesdb, requestOtherService, loadRequestedOtherServicesInpatient, deleteOrReverseOtherServicedb,
  loadMedicationByAge,
  loadNurseNotesdb, saveNurseNotedb, deleteNurseNotedb,
  loadVitalsHistorydb, saveVitalsdb, deleteVitalsdb,
  loadFBSHistorydb, saveFBSdb, deleteFBSdb,
  loadRBSHistorydb, saveRBSdb, deleteRBSdb,
  generateNote ,generateNurseNote ,generateVitals ,loadStaffNamesdb
} from "../BaseURL"; 

// History Dialogs (existing, read-only)
import VitalsHistoryDialogAdmission from "../Doctors/VitalsHistoryDialogAdmission";
import LabHistoryDialogAdmission from "../Doctors/LabHistoryDialogAdmission";
import ScanHistoryDialogAdmission from "../Doctors/ScanHistoryDialogAdmission";
import MedicalHistoryDialogAdmission from "../Doctors/MedicationHistoryDialogAdmission";

const OPDContextPanel = ({ opdContext }) => {
  const [expanded, setExpanded] = React.useState(false);

  const fields = [
    { label: 'Complaint',            value: opdContext?.complaint },
    { label: 'Patient History',      value: opdContext?.history   },
    { label: 'Physical Examination', value: opdContext?.exam      },
    { label: 'Other Documentation',  value: opdContext?.other     },
  ].filter(f => f.value?.trim());

  if (fields.length === 0) return null;

  return (
    <Paper elevation={0} sx={{ mb: 2, borderRadius: 2, border: '2px solid #1A9B7F', overflow: 'hidden' }}>
      <Box
        onClick={() => setExpanded(p => !p)}
        sx={{
          px: 2.5, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #1A9B7F 0%, #38ef7d 100%)',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TopicIcon sx={{ color: 'white', fontSize: 20 }} />
          <Typography fontWeight={700} fontSize="0.9rem" color="white">
            Pre-Admission OPD Consultation Note
          </Typography>
          <Chip
            label={`${fields.length} field${fields.length > 1 ? 's' : ''}`}
            size="small"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.25)', color: 'white' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
            {expanded ? 'Hide' : 'Show'}
          </Typography>
          {expanded
            ? <ExpandMoreIcon sx={{ color: 'white', fontSize: 20 }} />
            : <ChevronRightIcon sx={{ color: 'white', fontSize: 20 }} />
          }
        </Box>
      </Box>

      {expanded && (
        <Box sx={{ p: 2, bgcolor: '#f0fdf8' }}>
          <Grid container spacing={2} columns={12}>
            {fields.map((f, idx) => (
              <Grid item xs={12} md={6} key={idx} size={fields.length === 1 ? 12 : 6}>
                <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid #a7f3d0', bgcolor: 'white', height: '100%' }}>
                  <Typography
                    variant="caption" fontWeight={700} color="#1A9B7F"
                    sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem' }}
                  >
                    {f.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.65, color: '#1e293b', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {f.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
            These notes were recorded before the patient was admitted. They are read-only here.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const OutPatientWardRoundsDialog = ({
  open, onClose, fields, utils, patient,
  diagnosisOptions, selectedDiagnoses, setSelectedDiagnoses, saveOrUpdateDiagnosis,
  procedureOptions, selectedProcedures, setSelectedProcedures, saveOrUpdateProcedure,
  insuranceName ,
  opdContext
}) => {
  const { startDate, endDate } = fields;  // dat = startDate, d_dat = endDate
  const { suggestionsData, setHasEdited } = utils;

  const [actionDate, setActionDate] = useState(startDate || '');
  const isDetention = startDate && endDate && startDate === endDate;
  const isDischargeDate = actionDate && endDate && actionDate === endDate && !isDetention;
  const isOPD       = opdContext?.isOPD === true && !isDetention;
  const isInpatient = !isOPD && !isDetention;
  const [currentUser, setCurrentUser] = useState('');
  const [loading, setLoading] = useState(false);
  const isChild = Number(patient?.age) <= 12;


  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiIncludeSurgical, setAiIncludeSurgical] = useState(false);
  const [aiGeneratingNurseNote, setAiGeneratingNurseNote] = useState(false);
  const [aiGeneratingVitals, setAiGeneratingVitals] = useState(false);
  const [aiVitalsNurseName, setAiVitalsNurseName] = useState('');

  

  // ── Generation tracking (per dialog session) ──────────────────────
const [aiNoteGenCount,      setAiNoteGenCount]      = useState(0);
const [aiNurseGenCount,     setAiNurseGenCount]      = useState(0);
const [aiVitalsGenCount,    setAiVitalsGenCount]     = useState(0);
const [aiVitalsSeedOffset,  setAiVitalsSeedOffset]   = useState(0);

// How many AI note generations are allowed per session
const maxNoteGens   = isDetention ? 2 : (isOPD ? 1 : 99);
const maxVitalsGens = isDetention ? 1 : (isOPD ? 1 : 99);
const maxNurseGens  = isDetention ? 2 : (isOPD ? 1 : 99);

const noteGenBlocked   = aiNoteGenCount  >= maxNoteGens;
const vitalsGenBlocked = aiVitalsGenCount >= maxVitalsGens;
const nurseGenBlocked  = aiNurseGenCount  >= maxNurseGens;

const [staffDoctors, setStaffDoctors] = useState([]);
const [staffNurses,  setStaffNurses]  = useState([]);


 const getRandomGhanaDoctorName = () => {
  if (!staffDoctors.length) return currentUser || 'DOCTOR';
  return staffDoctors[Math.floor(Math.random() * staffDoctors.length)];
};

const [aiSaving, setAiSaving] = useState(false);


// ─── AI License helper ────────────────────────────────────────────────────────
const isAiAllowed = () => {
  try {
    return JSON.parse(localStorage.getItem('license_ai') || '{"allowed":true}').allowed !== false;
  } catch { return true; }
};

const showAiBlocked = () => {
  const reason = (() => {
    try { return JSON.parse(localStorage.getItem('license_ai') || '{}').reason || ''; } catch { return ''; }
  })();
  Swal.fire({
    icon: 'error',
    title: '🔒 AI Features License Expired',
    html: `<p style="color:#64748b;font-size:14px">${reason || 'Your AI license has expired. Contact TotalCare support.'}</p>`,
    confirmButtonText: 'OK',
    confirmButtonColor: '#d33',
  });
};


const handleSaveAIGeneratedNotes = async () => {
  if (!complaint && !exam && !other && !Surgical_Notes && !treatmentPlan) {
    ErrorMessage("Nothing to Save", "Please generate AI notes first.");
    return;
  }

  if (!actionDate) {
    ErrorMessage("Missing Date", "Please select an action date.");
    return;
  }

  setAiSaving(true);

  try {
    const randomDoctorName = getRandomGhanaDoctorName();

   // const selectedEntry = wardRoundsDataa.find(x => x.date === selectedDate);

const savePayload = {
  patientId: patient?.id,
  symptoms: complaint || "",
  ex_result: exam || "",
  Other_doc: other || "",
  Surgical_Notes: Surgical_Notes || "",
  plan: treatmentPlan || "",
  state: roundState || getRandomRoundState(),
  dat: actionDate,
  time: roundTime || getRandomTimeForRound(),
  doctor: randomDoctorName
};
    const res = await axios.post(saveAdmissionInfoRound, savePayload);

    if (res?.data?.success) {
      successMessage("Saved", "AI-generated notes saved successfully.");
      await loadWardRoundsDataFn();
      setSelectedDate(actionDate);
      setHasEdited(true);
    } else {
      ErrorMessage("Save Failed", res?.data?.message || "Unable to save AI-generated notes.");
    }
  } catch (error) {
    console.error("AI save error:", error);
    ErrorMessage("Error", "Failed to save AI-generated notes.");
  } finally {
    setAiSaving(false);
  }
};


  // ═══════════════════════════════════════════════════════════
  // Generate date range between dat and d_dat
  // If d_dat is not set (patient still admitted), use today
  // ═══════════════════════════════════════════════════════════
  const getTodayStr = () => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`;
  };

  const generateDateRange = (start, end) => {
    const dates = [];
    if (!start) return dates;

    const effectiveEnd = end || getTodayStr(); // if no d_dat, use today

    const startD = new Date(start + 'T00:00:00');
    const endD = new Date(effectiveEnd + 'T00:00:00');
    endD.setHours(23, 59, 59);

    const current = new Date(startD);
    while (current <= endD) {
      const y = current.getFullYear();
      const m = String(current.getMonth() + 1).padStart(2, '0');
      const d = String(current.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;

      const label = current.toLocaleDateString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
      });

      dates.push({ value: dateStr, label });
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const effectiveEndDate = endDate || getTodayStr();
  const dateOptions = generateDateRange(startDate, endDate);


  const buildClinicalContext = () => {
  const diagnosesText = (selectedDiagnoses || [])
    .map((d) => d?.name)
    .filter(Boolean)
    .join(", ");

  const proceduresText = (selectedProcedures || [])
    .map((p) => p?.name)
    .filter(Boolean)
    .join(", ");

  return {
    diagnosesText,
    proceduresText
  };
};

const getRandomRoundStyle = () => {
  const styles = [
    "brief and concise ward round note",
    "detailed consultant ward round note",
    "focused post-operative review note",
    "problem-oriented clinical progress note",
    "stable patient follow-up note",
    "acute review note with emphasis on symptoms and examination",
    "formal inpatient progress note",
    "short surgical ward review note"
  ];

  return styles[Math.floor(Math.random() * styles.length)];
};

const getRandomTimeForRound = () => {
  const times = [
    "07:15", "08:40", "09:20", "10:05", "11:30",
    "12:45", "14:10", "15:25", "16:40", "18:05", "19:20"
  ];

  return times[Math.floor(Math.random() * times.length)];
};

const getRandomRoundState = () => {
  const states = [
    "Morning Round",
    "Afternoon Round",
    "Evening Round",
    "Review Round"
  ];

  return states[Math.floor(Math.random() * states.length)];
};


const getRandomProgressHint = () => {
  const hints = [
    "patient improving clinically",
    "patient stable on current management",
    "persistent symptoms requiring reassessment",
    "post-procedure recovery ongoing",
    "mild improvement but still under observation",
    "needs close monitoring due to ongoing complaints",
    "symptoms resolving gradually",
    "patient tolerating treatment well",
    "clinical status stable with no new complaints",
    "patient responding well to ongoing management"
  ];

  return hints[Math.floor(Math.random() * hints.length)];
};


const handleAIGenerateNotes = async () => {
  if (!isAiAllowed()) { showAiBlocked(); return; }

  if (noteGenBlocked) {
    Swal.fire({
      icon: 'info',
      title: isDetention ? 'Detention Limit Reached' : 'Already Generated',
      html: `<p style="color:#64748b;font-size:14px">
        ${isDetention
          ? 'AI notes have already been generated twice for this detention visit.'
          : opdContext?.isOPD
            ? 'AI notes have already been generated for this outpatient visit.'
            : 'Discharge notes have already been generated.'}
        You can edit the notes directly in the fields above.
      </p>`,
      confirmButtonText: 'OK',
      confirmButtonColor: '#1976d2',
    });
    return;
  }

  const { diagnosesText, proceduresText } = buildClinicalContext();
  if (!diagnosesText && !proceduresText) {
    ErrorMessage("Missing Data", "Please select at least one diagnosis or procedure.");
    return;
  }

  setAiGenerating(true);

  try {
    const randomDoctorName   = getRandomGhanaDoctorName();
    const generatedRoundState = getRandomRoundState();
    const generatedRoundTime  = getRandomTimeForRound();
    const randomStyle         = getRandomRoundStyle();
    const randomProgressHint  = getRandomProgressHint();

    const payload = {
      patientId:   patient?.id,
      patientName: patient?.name,
      age:         patient?.age,
      gender:      patient?.gender,
      diagnoses:   (selectedDiagnoses || []).map(d => ({ id: d?.id, name: d?.name, code: d?.code })),
      procedures:  (selectedProcedures || []).map(p => ({ id: p?.id, name: p?.name, code: p?.code })),
      includeSurgicalNotes: aiIncludeSurgical,
      actionDate,
      roundState:   generatedRoundState,
      roundTime:    generatedRoundTime,
      doctor:       currentUser || randomDoctorName,
      noteStyle:    randomStyle,
      progressHint: randomProgressHint,
      isDischarge:  isDischargeDate,
      isDetention,
      isOPD:        opdContext?.isOPD || false,
      generationIndex: aiNoteGenCount,   // 0 = first, 1 = second (detention discharge)
      excludeNamesFromNotes: true,
      // ── Outpatient consultation context ──────────────────────
      opdComplaint: opdContext?.complaint || '',
      opdHistory:   opdContext?.history   || '',
      opdExam:      opdContext?.exam      || '',
      opdOther:     opdContext?.other     || '',
    };

    const res = await axios.post(generateNote, payload);

    if (res?.data?.success) {
      const ai = res.data.data || {};
      setComplaint(ai.complaint        || '');
      setExam(ai.physicalExam          || '');
      setOther(ai.otherDocumentation   || '');
      settreatmentPlan(ai.treatmentPlan || '');
      setSurgical_Notes(ai.surgicalNotes || '');
      setRoundState(generatedRoundState);
      setRoundTime(generatedRoundTime);
      setHasEdited(true);
      setAiNoteGenCount(c => c + 1);

      const successMsg = isDetention
        ? `Detention documentation generated (${aiNoteGenCount + 1}/2).`
        : isDischargeDate
          ? 'AI discharge documentation generated.'
          : opdContext?.isOPD
            ? 'Outpatient AI documentation generated.'
            : 'AI ward round notes generated.';

      successMessage('Generated', successMsg);
    } else {
      ErrorMessage('Generation Failed', res?.data?.message || 'Unable to generate notes.');
    }
  } catch (error) {
    if (error.response?.status === 403 && error.response?.data?.licenseBlocked) {
      showAiBlocked(); return;
    }
    console.error('AI generation error:', error);
    ErrorMessage('Error', 'Failed to generate AI notes.');
  } finally {
    setAiGenerating(false);
  }
};


const getSelectedDateNurseHistoryText = () => {
  return [...nurseNoteHistory]
    .filter((n) => n?.dat?.substring(0, 10) === actionDate)
    .sort((a, b) => {
      const dateA = new Date(`${a.dat} ${a.time || "00:00"}`);
      const dateB = new Date(`${b.dat} ${b.time || "00:00"}`);
      return dateB - dateA;
    })
    .map((n) => `${n.dat} ${n.time || ""} - ${n.user}: ${n.note}`)
    .join("\n");
};


const handleAIGenerateVitals = async () => {
  if (!isAiAllowed()) { showAiBlocked(); return; }

  if (vitalsGenBlocked) {
    Swal.fire({
      icon: 'info',
      title: 'Already Generated',
      html: `<p style="color:#64748b;font-size:14px">
        Vitals have already been AI-generated for this visit.
        Record additional vitals manually or use a new manual entry.
      </p>`,
      confirmButtonText: 'OK',
      confirmButtonColor: '#c62828',
    });
    return;
  }

  const nurseHistoryText = getSelectedDateNurseHistoryText();
  const hasContext = complaint || exam || other || treatmentPlan || nurseHistoryText
                  || opdContext?.complaint || opdContext?.exam;

  if (!hasContext) {
    ErrorMessage(
      'Missing Data',
      'Complaint, examination, or nurse note history is needed to generate vitals.'
    );
    return;
  }

  setAiGeneratingVitals(true);

  try {
    const randomNurseName = getRandomGhanaNurseName();
    setAiVitalsNurseName(randomNurseName);

    // Seed offset ensures each generation call gets slightly different values
    const newSeedOffset = aiVitalsSeedOffset + Math.floor(Math.random() * 50) + 10;
    setAiVitalsSeedOffset(newSeedOffset);

    const payload = {
        patientId:    patient?.id,
        age:          patient?.age,
        gender:       patient?.gender,
        actionDate,
        isChild,

        // ── Clinical context ──────────────────────────────────────
        complaint:          complaint    || opdContext?.complaint || '',
        physicalExam:       exam         || opdContext?.exam      || '',
        otherDocumentation: other        || opdContext?.other     || '',
        treatmentPlan:      treatmentPlan || '',
        nurseHistory:       nurseHistoryText || '',

        // ── Pre-admission OPD context ─────────────────────────────
        opdComplaint: opdContext?.complaint || '',
        opdHistory:   opdContext?.history   || '',
        opdExam:      opdContext?.exam      || '',

        // ── Previous vitals — last 3 records for trend context ───
        previousVitals: vitalsHistory
          .slice(0, 3)
          .map(v => ({
            date:            v.dat_vist,
            temperature:     v.tem,
            pulse:           v.pulse,
            respiratoryRate: v.res,
            bloodPressure:   v.pre,
            spO2:            v.spo2,
            weight:          v.we,
          })),

        // ── Flags ─────────────────────────────────────────────────
        isDischarge:   isDischargeDate,
        isDetention,
        isOPD:         opdContext?.isOPD || false,
        seedOffset:    newSeedOffset,
        generationIndex: aiVitalsGenCount,
        excludeNamesFromNotes: true,
      };

    const res = await axios.post(generateVitals, payload);

    if (res?.data?.success) {
      const ai = res.data.data || {};
      setTemperature(ai.temperature     || '');
      setPulse(ai.pulse                 || '');
      setRespiratoryRate(ai.respiratoryRate || '');
      // Never set BP for children — enforced client AND server side
      setBloodPressure(isChild ? '' : (ai.bloodPressure || ''));
      setSpO2(ai.spO2                   || '');
      setWeight(ai.weight               || '');
      setHeight(ai.height               || '');
      setAiVitalsGenCount(c => c + 1);

      successMessage(
        'Generated',
        isDischargeDate
          ? 'AI discharge vitals generated.'
          : opdContext?.isOPD
            ? 'Outpatient vitals generated.'
            : 'AI vitals generated.'
      );
    } else {
      ErrorMessage('Generation Failed', res?.data?.message || 'Unable to generate vitals.');
    }
  } catch (error) {
    if (error.response?.status === 403 && error.response?.data?.licenseBlocked) {
      showAiBlocked(); return;
    }
    console.error('AI vitals generation error:', error);
    ErrorMessage('Error', 'Failed to generate AI vitals.');
  } finally {
    setAiGeneratingVitals(false);
  }
};
  // ═══════════════════════════════════════════════════════════
  // CORE STATE: The date that controls EVERYTHING
  // Defaults to startDate (dat) — first date in range
  // ═══════════════════════════════════════════════════════════


const getRandomGhanaNurseName = () => {
  if (!staffNurses.length) return currentUser || 'NURSE';
  return staffNurses[Math.floor(Math.random() * staffNurses.length)];
};

const getRandomNurseTime = () => {
  const times = [
    "06:45", "07:20", "08:10", "09:35", "10:50",
    "12:15", "13:40", "15:05", "16:30", "18:10", "20:00"
  ];

  return times[Math.floor(Math.random() * times.length)];
};

const getSelectedDatePlanText = () => {
  return treatmentPlan || newtreatmentPlan || "";
};

const getSelectedDateDiagnosisText = () => {
  return (selectedDiagnoses || [])
    .map((d) => d?.name)
    .filter(Boolean)
    .join(", ");
};


const handleAIGenerateNurseNote = async () => {
  if (!isAiAllowed()) { showAiBlocked(); return; }

  if (nurseGenBlocked) {
    Swal.fire({
      icon: 'info',
      title: 'Already Generated',
      html: `<p style="color:#64748b;font-size:14px">
        A nurse note has already been AI-generated for this visit.
        Edit the note in the text area directly.
      </p>`,
      confirmButtonText: 'OK',
      confirmButtonColor: '#00838f',
    });
    return;
  }

  const diagnosisText = getSelectedDateDiagnosisText();
  const planText = getSelectedDatePlanText();

  if (!diagnosisText && !planText) {
    ErrorMessage('Missing Data', 'Diagnosis or treatment plan is needed to generate a nurse note.');
    return;
  }

  setAiGeneratingNurseNote(true);

  try {
    const randomNurseName = getRandomGhanaNurseName();
    const randomNurseTime = getRandomNurseTime();

    const payload = {
      patientId:   patient?.id,
      patientName: patient?.name,
      age:         patient?.age,
      gender:      patient?.gender,
      actionDate,
      selectedDate: actionDate,
      diagnosisText,
      treatmentPlan:  planText,
      complaint:      complaint || opdContext?.complaint || '',
      physicalExam:   exam      || opdContext?.exam      || '',
      otherDoc:       other     || opdContext?.other     || '',
      nurse:        randomNurseName,
      noteTime:     randomNurseTime,
      isDischarge:  isDischargeDate,
      isDetention,
      isOPD:        opdContext?.isOPD || false,
      generationIndex: aiNurseGenCount,
      excludeNamesFromNotes: true,
      proceduresText: (selectedProcedures || []).map(p => p?.name).filter(Boolean).join(', '),
        startDate,   // admission date
        endDate,     // discharge date
        opdComplaint: opdContext?.complaint || '',
        opdHistory:   opdContext?.history   || '',
        opdExam:      opdContext?.exam      || '',
        opdOther:     opdContext?.other     || '',
    };

    const res = await axios.post(generateNurseNote, payload);

    if (res?.data?.success) {
      const ai = res.data.data || {};
      setNurseNoteText(ai.nurseNote || '');
      setSelectedNurseNote(null);
      setAiNurseGenCount(c => c + 1);

      successMessage(
        'Generated',
        isDischargeDate
          ? 'AI discharge nurse note generated.'
          : opdContext?.isOPD
            ? 'Outpatient nurse note generated.'
            : 'AI nurse note generated.'
      );
    } else {
      ErrorMessage('Generation Failed', res?.data?.message || 'Unable to generate nurse note.');
    }
  } catch (error) {
    if (error.response?.status === 403 && error.response?.data?.licenseBlocked) {
      showAiBlocked(); return;
    }
    console.error('AI nurse note generation error:', error);
    ErrorMessage('Error', 'Failed to generate AI nurse note.');
  } finally {
    setAiGeneratingNurseNote(false);
  }
};

  // Ward Rounds States
  const [canEdit, setCanEdit] = useState(true);
  const [selectedDocName, setSelectedDocName] = useState('');
  const [wardRoundsDataa, setWardRoundsData] = useState([]);
  const [openDates, setOpenDates] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [complaint, setComplaint] = useState('');
  const [exam, setExam] = useState('');
  const [other, setOther] = useState('');
  const [treatmentPlan, settreatmentPlan] = useState('');
  const [Surgical_Notes, setSurgical_Notes] = useState('');

  // New entry states
  const [dialogOpen, setDialogOpen] = useState({ open: false, field: "" });
  const [dialogText, setDialogText] = useState("");
  const [Newcomplaint, setNewcomplaint] = useState('');
  const [newtreatmentPlan, setnewtreatmentPlan] = useState('');
  const [newexam, setexam] = useState('');
  const [newother, setother] = useState('');
  const [newsurgical, setsurgical] = useState('');
  const [roundState, setRoundState] = useState('');
  const [roundTime, setRoundTime] = useState('');

  // Active request tab: null | 'lab' | 'scan' | 'med' | 'service'
  const [activeRequest, setActiveRequest] = useState(null);

  // Lab States
  const [labs, setLabs] = useState([]);
  const [labSearchTerm, setLabSearchTerm] = useState('');
  const [requestedLabs, setRequestedLabs] = useState([]);
  const [labPage, setLabPage] = useState(0);
  const [reqLabPage, setReqLabPage] = useState(0);

  // Scan States
  const [scans, setScans] = useState([]);
  const [scanSearchTerm, setScanSearchTerm] = useState('');
  const [requestedScans, setRequestedScans] = useState([]);
  const [scanPage, setScanPage] = useState(0);
  const [reqScanPage, setReqScanPage] = useState(0);

  // Medication States
  const [medications, setMedications] = useState([]);
  const [medSearchTerm, setMedSearchTerm] = useState('');
  const [selectedMed, setSelectedMed] = useState(null);
  const [doseAmount, setDoseAmount] = useState('');
  const [doseUnit, setDoseUnit] = useState('');
  const [frequency, setFrequency] = useState('');
  const [days, setDays] = useState('');
  const [qty, setQty] = useState(0);
  const [route, setRoute] = useState('');
  const [autoCalculate, setAutoCalculate] = useState(true);
  const [requestedMeds, setRequestedMeds] = useState([]);
  const [medPage, setMedPage] = useState(0);
  const [reqMedPage, setReqMedPage] = useState(0);
  const [editingMedIndex, setEditingMedIndex] = useState(null);
  const [editMedData, setEditMedData] = useState(null);

  // Other Services States
  const [services, setServices] = useState([]);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [requestedServices, setRequestedServices] = useState([]);
  const [servicePage, setServicePage] = useState(0);
  const [reqServicePage, setReqServicePage] = useState(0);
  const [serviceQuantities, setServiceQuantities] = useState({});
  const [manualServiceName, setManualServiceName] = useState('');
  const [manualAmount, setManualAmount] = useState('');

  // Nurse Notes States
  const [nurseNoteText, setNurseNoteText] = useState('');
  const [nurseNoteHistory, setNurseNoteHistory] = useState([]);
  const [selectedNurseNote, setSelectedNurseNote] = useState(null);

  // Vitals States
  const [temperature, setTemperature] = useState('');
  const [pulse, setPulse] = useState('');
  const [respiratoryRate, setRespiratoryRate] = useState('');
  const [bloodPressure, setBloodPressure] = useState('');
  const [spO2, setSpO2] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [bmi, setBmi] = useState('');
  const [vitalsHistory, setVitalsHistory] = useState([]);
  const [selectedVitalRecord, setSelectedVitalRecord] = useState(null);

  // FBS/RBS States
  const [fbsValue, setFbsValue] = useState('');
  const [fbsRemarks, setFbsRemarks] = useState('');
  const [fbsHistory, setFbsHistory] = useState([]);
  const [selectedFbsRecord, setSelectedFbsRecord] = useState(null);
  const [rbsValue, setRbsValue] = useState('');
  const [rbsRemarks, setRbsRemarks] = useState('');
  const [rbsHistory, setRbsHistory] = useState([]);
  const [selectedRbsRecord, setSelectedRbsRecord] = useState(null);
  const [bloodSugarTab, setBloodSugarTab] = useState('fbs');

  // History Dialog States
  const [vitalsHistoryOpen, setVitalsHistoryOpen] = useState(false);
  const [labHistoryOpen, setLabHistoryOpen] = useState(false);
  const [scanHistoryOpen, setScanHistoryOpen] = useState(false);
  const [medHistoryOpen, setMedHistoryOpen] = useState(false);

  // ═══════════════════════════════════════════════════════════
  // INIT
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    const saved = localStorage.getItem('userInfo');
    if (saved) {
      const info = JSON.parse(saved);
      setCurrentUser(info.name || '');
    }
  }, []);

  // Reset actionDate to startDate whenever dialog opens or dates change
  useEffect(() => {
    if (open && startDate) {
      setActionDate(startDate);
    }
  }, [open, startDate]);

  useEffect(() => {
    if (!open) {
      // Reset all on close
      setSelectedDate(null);
      setComplaint(''); setExam(''); setOther('');
      settreatmentPlan(''); setSurgical_Notes('');
      setNewcomplaint(''); setnewtreatmentPlan('');
      setexam(''); setother(''); setsurgical('');
      setSelectedDocName(''); setCanEdit(true);
      setWardRoundsData([]); setOpenDates({});
      setActiveRequest(null);
      setRequestedLabs([]); setRequestedScans([]);
      setRequestedMeds([]); setRequestedServices([]);
      setNurseNoteText(''); setNurseNoteHistory([]); setSelectedNurseNote(null);
      setTemperature(''); setPulse(''); setRespiratoryRate('');
      setBloodPressure(''); setSpO2(''); setWeight('');
      setHeight(''); setBmi(''); setVitalsHistory([]); setSelectedVitalRecord(null);
      setFbsValue(''); setFbsRemarks(''); setFbsHistory([]); setSelectedFbsRecord(null);
      setRbsValue(''); setRbsRemarks(''); setRbsHistory([]); setSelectedRbsRecord(null);
      setBloodSugarTab('fbs');
      setAiNoteGenCount(0);
      setAiNurseGenCount(0);
      setAiVitalsGenCount(0);
      setAiVitalsSeedOffset(0);
    }
  }, [open]);

  useEffect(() => {
    if (open && patient?.id && startDate) {
      loadWardRoundsDataFn();
    }
  }, [open, patient?.id, startDate]);

  // ═══════════════════════════════════════════════════════════
  // RELOAD ALL REQUESTS when actionDate changes
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    if (open && patient?.id && actionDate) {
      loadRequestedLabsFn();
      loadRequestedScanFn();
      loadRequestedMedsFn();
      loadRequestedServicesFn();
      loadNurseNotesFn();
      loadVitalsFn();
      loadFBSFn();
      loadRBSFn();
    }
  }, [actionDate, open, patient?.id]);

  // Load available items on open
  useEffect(() => {
  if (open && patient?.id) {
    loadLabsFn();
    loadScansFn();
    loadMedicationsFn();
    loadOtherServicesFn();

    // ── Load real staff names ──────────────────────────────
    axios.get(loadStaffNamesdb)
      .then(res => {
        if (res.data.success) {
          setStaffDoctors(res.data.doctors || []);
          setStaffNurses(res.data.nurses   || []);
        }
      })
      .catch(e => console.error('Failed to load staff names:', e));
  }
}, [open, patient?.id]);

  // Auto-calculate medication qty
  useEffect(() => {
    if (!autoCalculate) return;
    const normalized = frequencyMap[(frequency || '').toLowerCase().trim()] || frequency;
    const perDay = frequencyMultiplier[normalized] ?? 1;
    const amt = parseFloat(doseAmount) || 0;
    const d = parseInt(days) || 0;
    if (amt && d) setQty(Math.ceil(amt * perDay * d));
    else setQty(0);
  }, [doseAmount, days, frequency, autoCalculate]);


  useEffect(() => {
  if (isChild) {
    setBloodPressure('');
  }
}, [isChild]);

  // ═══════════════════════════════════════════════════════════
  // WARD ROUNDS FUNCTIONS
  // ═══════════════════════════════════════════════════════════
  const loadWardRoundsDataFn = async () => {
    if (!patient?.id) return;
    try {
      const res = await axios.get(loadWardRoundsdb, {
        params: { patientId: patient.id, startDate }
      });
      if (res.data.success) setWardRoundsData(res.data.data);
    } catch (e) { console.error("Error loading ward rounds:", e); }
  };

  const groupByDate = (data) => {
    const groups = {};
    data.forEach(item => {
      if (!groups[item.date]) groups[item.date] = [];
      groups[item.date].push(item);
    });
    return groups;
  };

  const groupedRounds = groupByDate(wardRoundsDataa);

  const toggleDate = (date) => {
    setOpenDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  const handleDateSelect = (date, state, time) => {
    setSelectedDate(date);
    const entry = wardRoundsDataa.find(
      item => item.date === date && item.state === state && item.time === time
    );
    if (entry) {
      setComplaint(entry.complaint || "");
      setExam(entry.physicalExam || "");
      setOther(entry.otherDocumentation || "");
      settreatmentPlan(entry.plan || "");
      setSurgical_Notes(entry.surgicalNotes || "");
      setSelectedDocName(entry.doctor);
      setCanEdit(currentUser === entry.doctor);
      setHasEdited(true);
    }
  };

  let saveTimer = null;
  const autoSaveLoadedEntry = (field, value) => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(async () => {
      await axios.post(saveAdmissionInfoRound, {
        patientId: patient?.id,
        symptoms: field === "complaint" ? value : complaint,
        ex_result: field === "exam" ? value : exam,
        Other_doc: field === "other" ? value : other,
        Surgical_Notes: field === "surgical" ? value : Surgical_Notes,
        plan: field === "treatmentPlan" ? value : treatmentPlan,
        state: wardRoundsDataa.find(x => x.date === selectedDate)?.state,
        dat: selectedDate,
        time: wardRoundsDataa.find(x => x.date === selectedDate)?.time,
        doctor:getRandomGhanaDoctorName(),
      });
      await loadWardRoundsDataFn();
    }, 2000);
  };

  // New entry dialog
  const handleDialogOpen = (field) => {
    let currentText = "";
    if (field === "complaint") currentText = Newcomplaint;
    if (field === "exam") currentText = newexam;
    if (field === "other") currentText = newother;
    if (field === "treatmentPlan") currentText = newtreatmentPlan;
    if (field === "surgical") currentText = newsurgical;
    setDialogText(currentText);

    // Use actionDate instead of today
    setRoundTime(new Date().toTimeString().slice(0, 5));
    const hour = new Date().getHours();
    setRoundState(
      hour >= 6 && hour < 12 ? 'Morning Round' :
      hour >= 12 && hour < 18 ? 'Afternoon Round' : 'Evening Round'
    );
    setDialogOpen({ open: true, field });
  };

  const handleDialogClose = () => {
    setDialogOpen({ open: false, field: "" });
    setDialogText("");
  };


  const handleDialogSave = async () => {
    if (!roundState || !roundTime) {
      ErrorMessage("Missing Info", "Please select round state and time.");
      return;
    }
    try {
      await axios.post(saveAdmissionInfoRound, {
        patientId: patient?.id,
        symptoms: dialogOpen.field === "complaint" ? dialogText : Newcomplaint,
        ex_result: dialogOpen.field === "exam" ? dialogText : newexam,
        Other_doc: dialogOpen.field === "other" ? dialogText : newother,
        Surgical_Notes: dialogOpen.field === "surgical" ? dialogText : newsurgical,
        plan: dialogOpen.field === "treatmentPlan" ? dialogText : newtreatmentPlan,
        state: roundState,
        dat: actionDate,  // ← USES THE SELECTED ACTION DATE
        time: roundTime,
        doctor: currentUser,
      });
      await loadWardRoundsDataFn();
      setSelectedDate(actionDate);
    } catch (error) {
      ErrorMessage("Error", "Failed to save. Please try again.");
      return;
    }
    if (dialogOpen.field === "complaint") setNewcomplaint(dialogText);
    if (dialogOpen.field === "treatmentPlan") setnewtreatmentPlan(dialogText);
    if (dialogOpen.field === "exam") setexam(dialogText);
    if (dialogOpen.field === "other") setother(dialogText);
    if (dialogOpen.field === "surgical") setsurgical(dialogText);
    setHasEdited(true);
    setDialogText("");
    handleDialogClose();
  };

  // ═══════════════════════════════════════════════════════════
  // LAB FUNCTIONS (uses actionDate)
  // ═══════════════════════════════════════════════════════════
  const loadLabsFn = async () => {
    try {
      const res = await axios.get(loadLabdb, { params: { insurance: patient?.insurance } });
      if (res.data.success) setLabs(res.data.results);
    } catch (e) { console.error(e); }
  };

  const loadRequestedLabsFn = async () => {
    try {
      const res = await axios.get(loadRequestedLabsInpatients, {
        params: { patientId: patient?.id, dat: startDate, currentDate: actionDate }
      });
      if (res.data.success) setRequestedLabs(res.data.results);
    } catch (e) { console.error(e); }
  };

  const handleRequestLab = async (lab) => {
    const dup = requestedLabs.some(r => r.lab === lab.name && r.date === actionDate);
    if (dup) {
      const confirm = await Swal.fire({
        title: "Duplicate", text: `${lab.name} already requested on ${actionDate}. Request again?`,
        icon: "warning", showCancelButton: true, confirmButtonText: "Yes"
      });
      if (!confirm.isConfirmed) return;
    }
    setLoading(true);
    try {
      const res = await axios.post(requestLab, {
        patientId: patient?.id, dat: actionDate, patientInsurance: patient?.insurance,
        labName: lab.name, amount: lab.amount, base_cost: lab.base_cost,
        code: lab.code, currentUser, type: 'Lab'
      });
      if (res.data.success) loadRequestedLabsFn();
    } catch (e) { ErrorMessage("Error", "Failed to request lab"); }
    finally { setLoading(false); }
  };

  const handleDeleteLab = async (lab) => {
    if (lab.date?.substring(0, 10) !== actionDate) {
      ErrorMessage("Cannot Delete", "You can only delete requests for the selected date.");
      return;
    }
    const confirm = await Swal.fire({ title: "Delete?", text: `Delete "${lab.lab}"?`, icon: "warning", showCancelButton: true });
    if (!confirm.isConfirmed) return;
    setLoading(true);
    try {
      const res = await axios.post(deleteRequestedLabdb, {
        patientId: patient?.id, labName: lab.lab, dat: actionDate, currentUser
      });
      if (res.data.reversal) ErrorMessage("REVERSAL REQUIRED", res.data.reversal);
      else Swal.fire("Deleted", res.data.message, "success");
      loadRequestedLabsFn();
    } catch (e) { Swal.fire("Error", "Failed to delete", "error"); }
    finally { setLoading(false); }
  };

  // ═══════════════════════════════════════════════════════════
  // SCAN FUNCTIONS (uses actionDate)
  // ═══════════════════════════════════════════════════════════
  const loadScansFn = async () => {
    try {
      const res = await axios.get(loadAvailableScansdb, { params: { insurance: patient?.insurance } });
      if (res.data.success) setScans(res.data.results);
    } catch (e) { console.error(e); }
  };

  const loadRequestedScanFn = async () => {
    try {
      const res = await axios.get(loadRequestedScanInpatient, {
        params: { patientId: patient?.id, dat: startDate, currentDate: actionDate }
      });
      if (res.data.success) setRequestedScans(res.data.results);
    } catch (e) { console.error(e); }
  };

  const handleRequestScan = async (scan) => {
    const dup = requestedScans.some(r => (r.lab || r.scan) === scan.name && r.date === actionDate);
    if (dup) {
      const confirm = await Swal.fire({
        title: "Duplicate", text: `${scan.name} already requested on ${actionDate}. Request again?`,
        icon: "warning", showCancelButton: true, confirmButtonText: "Yes"
      });
      if (!confirm.isConfirmed) return;
    }
    setLoading(true);
    try {
      const res = await axios.post(requestLab, {
        patientId: patient?.id, patientInsurance: patient?.insurance,
        labName: scan.name, amount: scan.amount, base_cost: scan.tariff,
        code: scan.code, currentUser, type: 'Scan', dat: actionDate
      });
      if (res.data.success) loadRequestedScanFn();
    } catch (e) { ErrorMessage("Error", "Failed to request scan"); }
    finally { setLoading(false); }
  };

  const handleDeleteScan = async (scan) => {
    if (scan.date?.substring(0, 10) !== actionDate) {
      ErrorMessage("Cannot Delete", "You can only delete requests for the selected date.");
      return;
    }
    const confirm = await Swal.fire({ title: "Delete?", text: `Delete "${scan.lab}"?`, icon: "warning", showCancelButton: true });
    if (!confirm.isConfirmed) return;
    setLoading(true);
    try {
      const res = await axios.post(deleteRequestedLabdb, {
        patientId: patient?.id, labName: scan.lab, currentUser, dat: actionDate
      });
      if (res.data.reversal) ErrorMessage("REVERSAL REQUIRED", res.data.reversal);
      else Swal.fire("Deleted", res.data.message, "success");
      loadRequestedScanFn();
    } catch (e) { Swal.fire("Error", "Failed to delete", "error"); }
    finally { setLoading(false); }
  };

  // ═══════════════════════════════════════════════════════════
  // MEDICATION FUNCTIONS (uses actionDate)
  // ═══════════════════════════════════════════════════════════
  const loadMedicationsFn = async () => {
    try {
      const res = await axios.get(loadMedicationsdb, { params: { insurance: patient?.insurance } });
      if (res.data.success) setMedications(res.data.results);
    } catch (e) { console.error(e); }
  };

  const loadRequestedMedsFn = async () => {
    try {
      const res = await axios.get(loadRequestedMedsInpatient, {
        params: { patientId: patient?.id, dat: startDate, currentDate: actionDate }
      });
      if (res.data.success) setRequestedMeds(res.data.results);
    } catch (e) { console.error(e); }
  };

  const handleSelectMed = (med) => {
    setSelectedMed(med);
    setAutoCalculate(true);
    // Load last pattern
    // Prefer DOB so 11y11m children aren't routed to adult dosing tables.
    const ageGroup = isAdultFromDobOrAge(patient?.dob, patient?.age) ? "adult" : "child";
    axios.get(loadMedicationByAge, { params: { name: med.name, age: ageGroup } })
      .then(res => {
        if (res.data.success) {
          const p = res.data.med;
          setDoseAmount(p.dose || ''); setFrequency(p.freq || '');
          setDays(p.day_server || ''); setRoute(p.rou || ''); setQty(p.qua || 0);
        } else {
          setDoseAmount(''); setFrequency(''); setDays(''); setRoute(''); setQty(0);
        }
      }).catch(() => {});
  };

  const handleAddMed = async () => {
    if (!selectedMed) { ErrorMessage("Error", "Select a medication"); return; }
    if (!doseAmount || !frequency || !days || !route) { ErrorMessage("Error", "Fill all fields"); return; }
    if (qty <= 0) { ErrorMessage("Error", "Quantity must be > 0"); return; }

    const dup = requestedMeds.some(r => r.drug === selectedMed.name && r.date === actionDate);
    if (dup) {
      const confirm = await Swal.fire({
        title: "Duplicate", text: `${selectedMed.name} already requested on ${actionDate}. Request again?`,
        icon: "warning", showCancelButton: true, confirmButtonText: "Yes"
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      const res = await axios.post(addMedicationdb, {
        patientId: patient?.id, selectedMed, qty, frequency, route, doseAmount, days,
        currentUser, insurance: patient?.insurance, dat: actionDate, age: patient?.age
      });
      if (res.data.success) {
        loadRequestedMedsFn();
        clearMedForm();
      }
    } catch (e) { ErrorMessage("Error", "Failed to save medication"); }
    finally { setLoading(false); }
  };

  const handleDeleteMed = async (med) => {
    if (med.date?.substring(0, 10) !== actionDate) {
      ErrorMessage("Cannot Delete", "You can only delete medications for the selected date.");
      return;
    }
    const confirm = await Swal.fire({ title: "Delete?", text: `Delete "${med.drug}"?`, icon: "warning", showCancelButton: true });
    if (!confirm.isConfirmed) return;
    setLoading(true);
    try {
      const res = await axios.post(medicationdeletedb, {
        trans_code: med.trans_code, patientId: patient?.id, insurance: patient?.insurance, dat: actionDate
      });
      if (res.data.reversalRequired) Swal.fire("REVERSAL REQUIRED", res.data.message, "warning");
      else if (res.data.success) { Swal.fire("Deleted", res.data.message, "success"); loadRequestedMedsFn(); }
    } catch (e) { Swal.fire("Error", "Failed to delete", "error"); }
    finally { setLoading(false); }
  };

  const clearMedForm = () => {
    setSelectedMed(null); setDoseAmount(''); setDoseUnit('');
    setFrequency(''); setDays(''); setQty(0); setRoute('');
    setAutoCalculate(true); setEditingMedIndex(null); setEditMedData(null);
  };

  // ═══════════════════════════════════════════════════════════
  // OTHER SERVICES FUNCTIONS (uses actionDate)
  // ═══════════════════════════════════════════════════════════
  const loadOtherServicesFn = async () => {
    try {
      const res = await axios.get(loadOtherServicesdb, { params: { charge_status: "Manual" } });
      if (res.data.success) setServices(res.data.results);
    } catch (e) { console.error(e); }
  };

  const loadRequestedServicesFn = async () => {
    try {
      const res = await axios.get(loadRequestedOtherServicesInpatient, {
        params: { patientId: patient?.id, dat: startDate, currentDate: actionDate }
      });
      if (res.data.success) setRequestedServices(res.data.results);
    } catch (e) { console.error(e); }
  };

  const handleRequestService = async (srv) => {
    const dup = requestedServices.some(r => r.service === srv.name && r.date === actionDate);
    if (dup) {
      const confirm = await Swal.fire({
        title: "Duplicate", text: `${srv.name} already requested on ${actionDate}. Request again?`,
        icon: "warning", showCancelButton: true, confirmButtonText: "Yes"
      });
      if (!confirm.isConfirmed) return;
    }
    setLoading(true);
    try {
      const res = await axios.post(requestOtherService, {
        patientId: patient?.id, dat: actionDate, charge: srv.name,
        amount: srv.amount, doc_name: currentUser, from_user: currentUser,
        name: patient?.name, department: 'Doctor'
      });
      if (res.data.success) loadRequestedServicesFn();
    } catch (e) { ErrorMessage("Error", "Failed to request service"); }
    finally { setLoading(false); }
  };

  const handleDeleteService = async (req) => {
    if (req.date?.substring(0, 10) !== actionDate) {
      ErrorMessage("Cannot Delete", "You can only delete services for the selected date.");
      return;
    }
    const confirm = await Swal.fire({ title: "Delete?", text: `Delete "${req.service}"?`, icon: "warning", showCancelButton: true });
    if (!confirm.isConfirmed) return;
    setLoading(true);
    try {
      const res = await axios.post(deleteOrReverseOtherServicedb, {
        patientId: patient?.id, scanName: req.service, dat: actionDate, currentUser
      });
      if (res.data.reversal) ErrorMessage("REVERSAL REQUIRED", res.data.reversal);
      else Swal.fire("Deleted", res.data.message, "success");
      loadRequestedServicesFn();
    } catch (e) { Swal.fire("Error", "Failed to delete", "error"); }
    finally { setLoading(false); }
  };

  const handleManualService = async () => {
    if (!manualServiceName || !manualAmount) { ErrorMessage("Missing", "Enter name and amount"); return; }
    setLoading(true);
    try {
      const res = await axios.post(requestOtherService, {
        patientId: patient?.id, dat: actionDate, charge: manualServiceName,
        amount: Number(manualAmount), doc_name: currentUser, from_user: currentUser,
        name: patient?.name, department: "Doctor"
      });
      if (res.data.success) { loadRequestedServicesFn(); setManualServiceName(''); setManualAmount(''); }
    } catch (e) { ErrorMessage("Error", "Failed to request service"); }
    finally { setLoading(false); }
  };

  // ═══════════════════════════════════════════════════════════
  // AUTO-CALCULATE BMI
  // ═══════════════════════════════════════════════════════════
  useEffect(() => {
    const w = parseFloat(weight);
    const h = parseFloat(height) / 100;
    if (w > 0 && h > 0) setBmi((w / (h * h)).toFixed(1));
    else setBmi('');
  }, [weight, height]);

  // ═══════════════════════════════════════════════════════════
  // NURSE NOTES (uses actionDate) — same endpoints as InPatient.jsx
  // ═══════════════════════════════════════════════════════════
  const loadNurseNotesFn = async () => {
    if (!patient?.id) return;
    try {
      const res = await axios.get(loadNurseNotesdb, { params: { patientId: patient.id, visitDate: startDate } });
      if (res.data.success) setNurseNoteHistory(res.data.notes || []);
    } catch (e) { console.error("Error loading nurse notes:", e); }
  };

 const handleSaveNurseNote = async () => {
  if (!nurseNoteText.trim()) {
    ErrorMessage("Missing", "Please enter a nurse note.");
    return;
  }

  setLoading(true);

  try {
    const randomNurseName = getRandomGhanaNurseName();
    const randomNurseTime = getRandomNurseTime();

    const res = await axios.post(saveNurseNotedb, {
      patientId: patient?.id,
      note: nurseNoteText,
      user: randomNurseName,
      name: patient?.name,
      age: patient?.age,
      gen: patient?.gender,
      dat: actionDate,
      time: randomNurseTime
    });

    if (res.data.success) {
      successMessage("Saved", "Nurse note saved.");
      setNurseNoteText('');
      setSelectedNurseNote(null);
      loadNurseNotesFn();
    }
  } catch (e) {
    ErrorMessage("Error", "Failed to save nurse note.");
  } finally {
    setLoading(false);
  }
};

  // ═══════════════════════════════════════════════════════════
  // VITALS (uses actionDate) — same endpoints as InPatient.jsx
  // ═══════════════════════════════════════════════════════════
  const loadVitalsFn = async () => {
    if (!patient?.id) return;
    try {
      const res = await axios.get(loadVitalsHistorydb, { params: { patientId: patient.id, visitDate: startDate } });
      if (res.data.success) setVitalsHistory(res.data.vitals || []);
    } catch (e) { console.error("Error loading vitals:", e); }
  };

  const handleSaveVitals = async () => {
    if (!temperature) { ErrorMessage("Missing", "Temperature is required."); return; }
    setLoading(true);
    try {
      const calculatedBMI = (() => {
        const w = parseFloat(weight); const h = parseFloat(height) / 100;
        return (w > 0 && h > 0) ? (w / (h * h)).toFixed(2) : null;
      })();
     
      const res = await axios.post(saveVitalsdb, {
      patientId: patient?.id,
      trans_code: selectedVitalRecord?.trans_code || null,
      tem: temperature,
      pre: isChild ? null : (bloodPressure || null),
      res: respiratoryRate || null,
      pre: bloodPressure || null,
      spo2: spO2 || null,
      we: weight || null,
      high: height || null,
      bmi: calculatedBMI,
      user: aiVitalsNurseName || currentUser,
      name: patient?.name,
      age: patient?.age,
      gender: patient?.gender,
      status: patient?.insurance,
      dat_vist: actionDate,
      time: new Date().toTimeString().slice(0, 8)
    });
      if (res.data.success) { 
        successMessage("Saved", "Vitals recorded.");
         clearVitalsForm(); loadVitalsFn();
        setAiVitalsNurseName('');
         loadVitalsFn();
        }
    } catch (e) { ErrorMessage("Error", "Failed to save vitals."); }
    finally { setLoading(false); }
  };

const clearVitalsForm = () => {
  setTemperature('');
  setPulse('');
  setRespiratoryRate('');
  setBloodPressure('');
  setSpO2('');
  setWeight('');
  setHeight('');
  setBmi('');
  setSelectedVitalRecord(null);
  setAiVitalsNurseName('');
};

  // ── SELECT HANDLERS (populate forms from history cards) ──
  const handleSelectNurseNote = (note) => {
    setSelectedNurseNote(note);
    setNurseNoteText(note.note || '');
  };

  const handleSelectVitalRecord = (v) => {
    setSelectedVitalRecord(v);
    setTemperature(v.tem || '');
    setPulse(v.pulse || '');
    setRespiratoryRate(v.res || '');
    setBloodPressure(isChild ? '' : (v.pre || ''));
    setSpO2(v.spo2 || '');
    setWeight(v.we || '');
    setHeight(v.high || '');
    setBmi(v.bmi || '');
  };

  const handleSelectFbsRecord = (f) => {
    setSelectedFbsRecord(f);
    setFbsValue(f.FBS || '');
    setFbsRemarks(f.remark || '');
  };

  const handleSelectRbsRecord = (r) => {
    setSelectedRbsRecord(r);
    setRbsValue(r.RBS || '');
    setRbsRemarks(r.remark || '');
  };

  // ═══════════════════════════════════════════════════════════
  // FBS / RBS (uses actionDate) — same endpoints as InPatient.jsx
  // ═══════════════════════════════════════════════════════════
  const loadFBSFn = async () => {
    if (!patient?.id) return;
    try {
      const res = await axios.get(loadFBSHistorydb, { params: { patientId: patient.id, visitDate: startDate } });
      if (res.data.success) setFbsHistory(res.data.fbs || []);
    } catch (e) { console.error("Error loading FBS:", e); }
  };

  const handleSaveFBS = async () => {
    if (!fbsValue) { ErrorMessage("Missing", "FBS value is required."); return; }
    setLoading(true);
    try {
      const res = await axios.post(saveFBSdb, {
        patientId: patient?.id,
        trans_code: selectedFbsRecord?.trans_code || null,
        fbs: fbsValue,
        remark: fbsRemarks,
        dat: actionDate,
        time: new Date().toTimeString().slice(0, 8)
      });
      if (res.data.success) { successMessage("Saved", "FBS recorded."); setFbsValue(''); setFbsRemarks(''); setSelectedFbsRecord(null); loadFBSFn(); }
    } catch (e) { ErrorMessage("Error", "Failed to save FBS."); }
    finally { setLoading(false); }
  };

  const loadRBSFn = async () => {
    if (!patient?.id) return;
    try {
      const res = await axios.get(loadRBSHistorydb, { params: { patientId: patient.id, visitDate: startDate } });
      if (res.data.success) setRbsHistory(res.data.rbs || []);
    } catch (e) { console.error("Error loading RBS:", e); }
  };

  const handleSaveRBS = async () => {
    if (!rbsValue) { ErrorMessage("Missing", "RBS value is required."); return; }
    setLoading(true);
    try {
      const res = await axios.post(saveRBSdb, {
        patientId: patient?.id,
        trans_code: selectedRbsRecord?.trans_code || null,
        rbs: rbsValue,
        insulin: '',
        remark: rbsRemarks,
        dat: actionDate,
        time: new Date().toTimeString().slice(0, 8)
      });
      if (res.data.success) { successMessage("Saved", "RBS recorded."); setRbsValue(''); setRbsRemarks(''); setSelectedRbsRecord(null); loadRBSFn(); }
    } catch (e) { ErrorMessage("Error", "Failed to save RBS."); }
    finally { setLoading(false); }
  };

  const getFBSFlag = (val) => { const v = parseFloat(val); if (!v) return null; if (v < 3.9) return { label: 'Low (Hypoglycemia)', color: '#d32f2f' }; if (v <= 5.6) return { label: 'Normal', color: '#2e7d32' }; if (v <= 6.9) return { label: 'Pre-diabetic', color: '#ed6c02' }; return { label: 'Diabetic', color: '#d32f2f' }; };
  const getRBSFlag = (val) => { const v = parseFloat(val); if (!v) return null; if (v < 3.9) return { label: 'Low', color: '#d32f2f' }; if (v <= 7.8) return { label: 'Normal', color: '#2e7d32' }; if (v <= 11.0) return { label: 'Pre-diabetic', color: '#ed6c02' }; return { label: 'Diabetic', color: '#d32f2f' }; };

  // ═══════════════════════════════════════════════════════════
  // FIELD CONFIGS
  // ═══════════════════════════════════════════════════════════
  const fieldConfigs = [
    { field: "complaint", label: "Complaint", value: complaint, setter: setComplaint, buttonText: "New Complaint", gradient: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)", icon: <MedicalServicesIcon /> },
    { field: "exam", label: "Physical Examination", value: exam, setter: setExam, buttonText: "New Physical Exam", gradient: "linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)", icon: <LocalHospitalIcon /> },
    { field: "other", label: "Other Documentation", value: other, setter: setOther, buttonText: "New Documentation", gradient: "linear-gradient(135deg, #ef6c00 0%, #ffa726 100%)", icon: <NoteAddIcon /> },
    { field: "surgical", label: "Surgical Notes", value: Surgical_Notes, setter: setSurgical_Notes, buttonText: "New Surgical Note", gradient: "linear-gradient(135deg, #6a1b9a 0%, #ab47bc 100%)", icon: <ScienceIcon /> },
    { field: "treatmentPlan", label: "Treatment Plan", value: treatmentPlan, setter: settreatmentPlan, buttonText: "New Treatment Plan", gradient: "linear-gradient(135deg, #c2185b 0%, #f06292 100%)", icon: <MedicalServicesIcon /> },
  ];

  const requestTabs = [
    { key: 'lab', label: 'Laboratory', icon: <ScienceIcon />, color: '#1976d2' },
    { key: 'scan', label: 'Scan', icon: <CameraAlt />, color: '#2e7d32' },
    { key: 'med', label: 'Medication', icon: <Medication />, color: '#e65100' },
    { key: 'service', label: 'Other Services', icon: <NoteAddIcon />, color: '#6a1b9a' },
    { key: 'nurse', label: 'Nurse Note', icon: <EditNoteIcon />, color: '#00838f' },
    { key: 'vitals', label: 'Vitals', icon: <MonitorHeartIcon />, color: '#c62828' },
    { key: 'bloodsugar', label: 'FBS / RBS', icon: <ScienceIcon />, color: '#33691e' },
  ];

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth TransitionComponent={Fade} transitionDuration={300}>
        {/* HEADER */}
        <DialogTitle sx={{ background: "linear-gradient(135deg, #19b934 0%, #11998e 100%)", color: "white", pb: 1, pt: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "1.2rem", color: "white", border: "2px solid rgba(255,255,255,0.5)" }}>
                {patient?.name?.split(" ").map(n => n[0]).join("").toUpperCase()}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: "1.3rem" }}>{patient?.name?.toUpperCase()}</Typography>
                <Typography sx={{ fontSize: "0.95rem", opacity: 0.95, mt: 0.5 }}>
                  ID: {patient?.id} • Age: {patient?.age} • {patient?.gender?.toUpperCase()} • {patient?.phone}
                </Typography>
              </Box>
            </Box>
            <IconButton sx={{ color: "white" }} onClick={onClose}><CloseIcon /></IconButton>
          </Box>

          {/* ★★★ DATE SELECTOR BAR ★★★ */}
          <Paper elevation={3} sx={{ mt: 1.5, mb: 0.5, mx: -1, px: 3, py: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'rgba(255,255,255,0.95)', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon sx={{ color: '#e65100', fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight={700} color="#333">
                Action Date:
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <Select
                value={actionDate}
                onChange={(e) => setActionDate(e.target.value)}
                sx={{
                  borderRadius: 2, fontWeight: 700, fontSize: '0.95rem',
                  bgcolor: '#fff3e0',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e65100' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#e65100', borderWidth: 2 },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bf360c' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 350, borderRadius: 2 }
                  }
                }}
              >
                {dateOptions.map((opt) => {
                  const isStart = opt.value === startDate;
                  const isEnd = opt.value === effectiveEndDate;
                  const isToday = opt.value === getTodayStr();
                  return (
                    <MenuItem key={opt.value} value={opt.value} sx={{
                      py: 1, fontSize: '0.88rem',
                      fontWeight: (isStart || isEnd) ? 700 : 400,
                      bgcolor: isStart ? '#e8f5e9' : (isEnd && endDate) ? '#fce4ec' : isToday ? '#fff3e0' : 'inherit',
                      borderLeft: isStart ? '4px solid #2e7d32' : isToday ? '4px solid #e65100' : '4px solid transparent',
                      '&.Mui-selected': { bgcolor: '#ffe0b2', fontWeight: 700 },
                      '&.Mui-selected:hover': { bgcolor: '#ffcc80' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography fontSize="0.88rem" fontWeight={(isStart || isEnd || isToday) ? 700 : 400}>
                          {opt.label}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {isStart && (
                            <Chip label="Admission" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#2e7d32', color: 'white' }} />
                          )}
                          {isEnd && endDate && (
                            <Chip label="Discharge" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#c62828', color: 'white' }} />
                          )}
                          {isToday && (
                            <Chip label="Today" size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#e65100', color: 'white' }} />
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  );
                })}
                {dateOptions.length === 0 && (
                  <MenuItem disabled>
                    <Typography color="text.secondary" fontSize="0.85rem">No dates available</Typography>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <Chip label={`Admitted: ${startDate || '—'}`} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
            <Chip
              label={endDate ? `Discharged: ${endDate}` : 'Still Admitted'}
              size="small"
              sx={{ bgcolor: endDate ? '#fce4ec' : '#fff3e0', color: endDate ? '#c62828' : '#e65100', fontWeight: 600 }}
            />
            <Chip label={`${dateOptions.length} day${dateOptions.length !== 1 ? 's' : ''}`} size="small" variant="outlined" sx={{ fontWeight: 600, color: '#555' }} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              All entries & requests use this date
            </Typography>
          </Paper>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5, bgcolor: "#f5f7fa" }}>
          {loading && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.3)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={50} />
            </Box>
          )}

          {!canEdit && selectedDate && (
            <Alert severity="warning" icon={<LockIcon />} sx={{ mb: 2, borderRadius: 2 }}>
              <strong>View Only:</strong> Entry by {selectedDocName}. You can only edit your own entries.
            </Alert>
          )}

            {!isOPD && !isDetention && (
            opdContext?.complaint || opdContext?.history || opdContext?.exam || opdContext?.other
          ) && (
            <OPDContextPanel opdContext={opdContext} />
          )}

          <Grid container spacing={2.5} columns={{ xs: 4, sm: 8, md: 12 }}>
            {/* LEFT: Dates Sidebar */}
            <Grid item xs={12} md={2} size={2}>
              <Card sx={{ borderRadius: 3, maxHeight: "70vh", overflowY: "auto", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <CalendarTodayIcon color="primary" sx={{ fontSize: 20 }} />
                    <Typography fontWeight={700} fontSize="0.95rem">Ward Round Dates</Typography>
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />

                  {Object.keys(groupedRounds).length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>No entries yet</Typography>
                  )}

                  {Object.keys(groupedRounds).map((date, idx) => (
                    <Box key={idx} sx={{ mb: 1 }}>
                      <Paper elevation={openDates[date] ? 2 : 0} onClick={() => toggleDate(date)}
                        sx={{ p: 1.25, borderRadius: 1.5, cursor: "pointer", background: openDates[date] ? "#1976d2" : "#f5f5f5", color: openDates[date] ? "white" : "#333", transition: "all 0.2s", border: openDates[date] ? "none" : "1px solid #e0e0e0", display: "flex", alignItems: "center", justifyContent: "space-between", "&:hover": { background: openDates[date] ? "#1565c0" : "#eee" } }}>
                        <Typography fontWeight={600} fontSize="0.85rem">{date}</Typography>
                        {openDates[date] ? <ExpandMoreIcon sx={{ fontSize: "1rem" }} /> : <ChevronRightIcon sx={{ fontSize: "1rem" }} />}
                      </Paper>
                      {openDates[date] && groupedRounds[date].map((item, i) => (
                        <Paper key={i} onClick={() => handleDateSelect(item.date, item.state, item.time)} elevation={0}
                          sx={{ p: 1, ml: 1.5, mt: 0.5, borderRadius: 1, cursor: "pointer", border: selectedDate === item.date && selectedDocName === item.doctor ? "2px solid #1976d2" : "1px solid #e0e0e0", background: selectedDate === item.date && selectedDocName === item.doctor ? "#e3f2fd" : "white", "&:hover": { background: "#f5f5f5" }, transition: "all 0.2s" }}>
                          <Typography fontSize="0.8rem" fontWeight={500}>{item.doctor}</Typography>
                          <Typography fontSize="0.72rem" color="text.secondary">{item.state}</Typography>
                        </Paper>
                      ))}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            {/* RIGHT: Fields + Requests */}
            <Grid item xs={12} md={10} size={10}>
              <Grid container spacing={2} columns={12}>
                {/* Ward Round Fields */}
                {fieldConfigs.map((cfg) => (
                  <Grid item xs={12} md={2.4} key={cfg.field}>
                    <Card sx={{ height: "100%", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
                      <CardContent sx={{ p: 2 }}>
                        <TextField label={cfg.label} multiline rows={4} fullWidth value={cfg.value}
                          onChange={(e) => { if (canEdit) { cfg.setter(e.target.value); setHasEdited(true); autoSaveLoadedEntry(cfg.field, e.target.value); } }}
                          disabled={!canEdit} placeholder={`Enter ${cfg.label.toLowerCase()}...`}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.85rem' } }} />
                        <Button variant="contained" size="small" startIcon={cfg.icon} fullWidth onClick={() => handleDialogOpen(cfg.field)}
                          sx={{ mt: 1.5, background: cfg.gradient, color: "white", borderRadius: 1.5, textTransform: "none", fontWeight: 600, py: 0.75, fontSize: '0.8rem' }}>
                          {cfg.buttonText}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {/* Diagnosis */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Diagnosis(es)</Typography>
                  <Autocomplete multiple options={diagnosisOptions} value={selectedDiagnoses || []}
                    onChange={(e, v) => { setSelectedDiagnoses(v); saveOrUpdateDiagnosis(v); }}
                    getOptionLabel={(o) => o?.name || ""} filterSelectedOptions
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    filterOptions={(opts, { inputValue }) => { const s = inputValue.toLowerCase(); return opts.filter(o => o.name.toLowerCase().includes(s) || o.code.toLowerCase().includes(s)); }}
                    renderInput={(params) => <TextField {...params} placeholder="Search diagnoses..." size="small" />}
                    renderTags={(val, getTagProps) => val.map((o, i) => <Chip key={o.id} label={o.name} {...getTagProps({ index: i })} size="small" />)}
                  />
                </Grid>

                {/* Procedures */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Procedure(s)</Typography>
                  <Autocomplete multiple options={procedureOptions} value={selectedProcedures || []}
                    onChange={(e, v) => { setSelectedProcedures(v); saveOrUpdateProcedure(v); }}
                    getOptionLabel={(o) => o?.name || ""} filterSelectedOptions
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    filterOptions={(opts, { inputValue }) => { const s = inputValue.toLowerCase(); return opts.filter(o => o.name?.toLowerCase().includes(s)); }}
                    renderInput={(params) => <TextField {...params} placeholder="Search procedures..." size="small" />}
                    renderTags={(val, getTagProps) => val.map((o, i) => <Chip key={o.id} label={o.name} {...getTagProps({ index: i })} size="small" />)}
                  />
                </Grid>

<Grid item xs={12}>
  <Paper
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: noteGenBlocked ? '#e0e0e0' : '#d0e2ff',
      bgcolor: noteGenBlocked ? '#fafafa' : '#f8fbff',
      opacity: noteGenBlocked ? 0.85 : 1,
      transition: 'all 0.3s'
    }}
  >
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            AI Documentation Generator
          </Typography>
          {/* Generation limit badges */}
          {opdContext?.isOPD && !isDetention && (
            <Chip
              label={aiNoteGenCount >= 1 ? 'Generated ✓' : 'OPD — 1 generation'}
              size="small"
              sx={{
                height: 20, fontSize: '0.65rem', fontWeight: 700,
                bgcolor: aiNoteGenCount >= 1 ? '#e8f5e9' : '#e3f2fd',
                color:   aiNoteGenCount >= 1 ? '#2e7d32' : '#1976d2',
              }}
            />
          )}
          {isDetention && (
            <Chip
              label={`Detention: ${aiNoteGenCount}/2 generated`}
              size="small"
              sx={{
                height: 20, fontSize: '0.65rem', fontWeight: 700,
                bgcolor: aiNoteGenCount >= 2 ? '#fce4ec' : '#fff3e0',
                color:   aiNoteGenCount >= 2 ? '#c62828' : '#e65100',
              }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {isDetention
            ? `Detention visit — same-day. ${aiNoteGenCount === 0 ? 'First generation: admission.' : aiNoteGenCount === 1 ? 'Second generation available: discharge summary.' : 'Both generations used.'}`
            : isDischargeDate
              ? 'Discharge date — AI will generate discharge documentation.'
              : opdContext?.isOPD
                ? 'Outpatient visit — generates once using consultation context.'
                : 'Inpatient ward round — generates clinical progress note.'
          }
        </Typography>
        {opdContext?.isOPD && opdContext?.complaint && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
            Context loaded: complaint, history, exam from consultation tab.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={<Checkbox checked={aiIncludeSurgical} onChange={(e) => setAiIncludeSurgical(e.target.checked)} size="small" />}
          label={<Typography variant="caption">Include Surgical Notes</Typography>}
        />

        <Button
          variant="contained"
          onClick={handleAIGenerateNotes}
          disabled={
            aiGenerating || noteGenBlocked ||
            ((!selectedDiagnoses || selectedDiagnoses.length === 0) &&
             (!selectedProcedures || selectedProcedures.length === 0))
          }
          startIcon={aiGenerating ? <CircularProgress size={16} color="inherit" /> : noteGenBlocked ? <LockIcon /> : <AutoAwesomeIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            px: 2.5,
            background: noteGenBlocked
              ? '#e0e0e0'
              : 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)'
          }}
        >
          {aiGenerating
            ? 'Generating...'
            : noteGenBlocked
              ? (isDetention && aiNoteGenCount < 2 ? 'Generate Discharge' : 'Already Generated')
              : (isDetention && aiNoteGenCount === 1 ? 'Generate Discharge Notes' : 'Generate with AI')
          }
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={handleSaveAIGeneratedNotes}
          disabled={aiSaving || (!complaint && !exam && !other && !Surgical_Notes && !treatmentPlan)}
          startIcon={aiSaving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 2.5 }}
        >
          {aiSaving ? 'Saving...' : 'Save AI Notes'}
        </Button>
      </Box>
    </Box>
  </Paper>
</Grid>

                {/* History Buttons */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Admission History</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      {[
                        { label: 'Vitals', onClick: () => setVitalsHistoryOpen(true), color: '#e53935' },
                        { label: 'Labs', onClick: () => setLabHistoryOpen(true), color: '#1976d2' },
                        { label: 'Scans', onClick: () => setScanHistoryOpen(true), color: '#2e7d32' },
                        { label: 'Medications', onClick: () => setMedHistoryOpen(true), color: '#e65100' },
                      ].map((btn) => (
                        <Button key={btn.label} variant="outlined" size="small" onClick={btn.onClick}
                          sx={{ textTransform: 'none', fontWeight: 600, borderColor: '#e0e0e0', color: '#555', borderRadius: 1.5, '&:hover': { borderColor: btn.color, color: btn.color } }}>
                          {btn.label}
                        </Button>
                      ))}
                    </Box>
                  </Paper>
                </Grid>

                {/* ═══════ REQUEST TABS ═══════ */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Request Services for <Chip label={dateOptions.find(d => d.value === actionDate)?.label || actionDate} size="small" sx={{ fontWeight: 700, bgcolor: '#fff3e0', color: '#e65100', ml: 1 }} />
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {requestTabs.map((tab) => (
                        <Button key={tab.key} variant={activeRequest === tab.key ? 'contained' : 'outlined'}
                          startIcon={tab.icon} size="small"
                          onClick={() => setActiveRequest(activeRequest === tab.key ? null : tab.key)}
                          sx={{
                            textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2,
                            ...(activeRequest === tab.key ? { bgcolor: tab.color, '&:hover': { bgcolor: tab.color } } : { borderColor: '#e0e0e0', color: '#555' })
                          }}>
                          {tab.label}
                        </Button>
                      ))}
                    </Box>

                    {/* ── LAB REQUEST PANEL ── */}
                    {activeRequest === 'lab' && (
                      <Grid container spacing={2} columns={12}>
                        <Grid item xs={5}>
                          <TextField fullWidth size="small" placeholder="Search labs..." value={labSearchTerm} onChange={(e) => setLabSearchTerm(e.target.value)} sx={{ mb: 1 }} />
                          <TableContainer sx={{ maxHeight: 280, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Table size="small" stickyHeader>
                              <TableHead><TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Lab</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Request</TableCell>
                              </TableRow></TableHead>
                              <TableBody>
                                {labs.filter(l => l.name.toLowerCase().includes(labSearchTerm.toLowerCase()))
                                  .slice(labPage * 8, labPage * 8 + 8).map((lab, i) => (
                                  <TableRow key={i} hover>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{lab.name}</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{formatWithOutSom(lab.amount)}</TableCell>
                                    <TableCell align="center"><IconButton size="small" color="primary" onClick={() => handleRequestLab(lab)}><SendIcon fontSize="small" /></IconButton></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>Requested Labs</Typography>
                          <TableContainer sx={{ maxHeight: 280, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Table size="small" stickyHeader>
                              <TableHead><TableRow>
                                {['Date', 'Lab', 'Amount', 'By', 'Status', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>)}
                              </TableRow></TableHead>
                              <TableBody>
                                {requestedLabs.slice(reqLabPage * 8, reqLabPage * 8 + 8).map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{r.date}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{r.lab}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{formatWithOutSom(r.amount)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{r.requestedBy}</TableCell>
                                    <TableCell><Chip size="small" label={r.status} color={r.status === 'paid' ? 'success' : 'warning'} sx={{ fontSize: '0.65rem' }} /></TableCell>
                                    <TableCell>
                                      {r.date?.substring(0, 10) === actionDate ? (
                                        <IconButton size="small" color="error" onClick={() => handleDeleteLab(r)}><Close fontSize="small" /></IconButton>
                                      ) : <IconButton size="small" disabled><LockIcon fontSize="small" /></IconButton>}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      </Grid>
                    )}

                    {/* ── SCAN REQUEST PANEL ── */}
                    {activeRequest === 'scan' && (
                      <Grid container spacing={2} columns={12}>
                        <Grid item xs={5}>
                          <TextField fullWidth size="small" placeholder="Search scans..." value={scanSearchTerm} onChange={(e) => setScanSearchTerm(e.target.value)} sx={{ mb: 1 }} />
                          <TableContainer sx={{ maxHeight: 280, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Table size="small" stickyHeader>
                              <TableHead><TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Scan</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Request</TableCell>
                              </TableRow></TableHead>
                              <TableBody>
                                {scans.filter(s => s.name.toLowerCase().includes(scanSearchTerm.toLowerCase()))
                                  .slice(scanPage * 8, scanPage * 8 + 8).map((scan, i) => (
                                  <TableRow key={i} hover>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{scan.name}</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{formatWithOutSom(scan.amount)}</TableCell>
                                    <TableCell align="center"><IconButton size="small" color="primary" onClick={() => handleRequestScan(scan)}><SendIcon fontSize="small" /></IconButton></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>Requested Scans</Typography>
                          <TableContainer sx={{ maxHeight: 280, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Table size="small" stickyHeader>
                              <TableHead><TableRow>
                                {['Date', 'Scan', 'Amount', 'By', 'Status', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>)}
                              </TableRow></TableHead>
                              <TableBody>
                                {requestedScans.slice(reqScanPage * 8, reqScanPage * 8 + 8).map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{r.date}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{r.lab}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{formatWithOutSom(r.amount)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{r.requestedBy}</TableCell>
                                    <TableCell><Chip size="small" label={r.status} color={r.status === 'paid' ? 'success' : 'warning'} sx={{ fontSize: '0.65rem' }} /></TableCell>
                                    <TableCell>
                                      {r.date?.substring(0, 10) === actionDate ? (
                                        <IconButton size="small" color="error" onClick={() => handleDeleteScan(r)}><Close fontSize="small" /></IconButton>
                                      ) : <IconButton size="small" disabled><LockIcon fontSize="small" /></IconButton>}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      </Grid>
                    )}

                    {/* ── MEDICATION REQUEST PANEL ── */}
                    {activeRequest === 'med' && (
                      <Grid container spacing={2} columns={12}>
                        <Grid item xs={3}>
                          <TextField fullWidth size="small" placeholder="Search meds..." value={medSearchTerm} onChange={(e) => setMedSearchTerm(e.target.value)} sx={{ mb: 1 }} />
                          <TableContainer sx={{ maxHeight: 300, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Table size="small" stickyHeader>
                              <TableHead><TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Medication</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Select</TableCell>
                              </TableRow></TableHead>
                              <TableBody>
                                {medications.filter(m => m.name.toLowerCase().includes(medSearchTerm.toLowerCase()))
                                  .slice(medPage * 10, medPage * 10 + 10).map((med, i) => (
                                  <TableRow key={i} hover>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{med.name}</TableCell>
                                    <TableCell align="center"><IconButton size="small" color="primary" onClick={() => handleSelectMed(med)}><SendIcon fontSize="small" /></IconButton></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                        <Grid item xs={3}>
                          <Paper sx={{ p: 1.5, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>Medication Form</Typography>
                            <TextField size="small" fullWidth label="Selected" value={selectedMed?.name || ''} disabled margin="dense"
                              sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#000' } }} />
                            <TextField size="small" fullWidth label="Dose Amount" value={doseAmount} onChange={e => setDoseAmount(e.target.value)} margin="dense" />
                            <FormControl fullWidth size="small" margin="dense">
                              <InputLabel>Frequency</InputLabel>
                              <Select value={frequency} label="Frequency" onChange={e => setFrequency(e.target.value)}>
                                {standardFrequencies.map(f => <MenuItem key={f} value={f} sx={{ fontSize: '0.8rem' }}>{f}</MenuItem>)}
                              </Select>
                            </FormControl>
                            <TextField size="small" fullWidth type="number" label="Days" value={days} onChange={e => setDays(e.target.value)} margin="dense" />
                            <FormControl fullWidth size="small" margin="dense">
                              <InputLabel>Route</InputLabel>
                              <Select value={route} label="Route" onChange={e => setRoute(e.target.value)}>
                                {MEDICATION_ROUTES.map(r => <MenuItem key={r} value={r} sx={{ fontSize: '0.8rem' }}>{r}</MenuItem>)}
                              </Select>
                            </FormControl>
                            <TextField size="small" fullWidth type="number" label="Qty" value={qty} disabled={autoCalculate} margin="dense"
                              onChange={e => !autoCalculate && setQty(Number(e.target.value))}
                              sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#000' } }} />
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              <Button size="small" variant="contained" fullWidth onClick={handleAddMed} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>Request</Button>
                              <Button size="small" variant="outlined" color="error" fullWidth onClick={clearMedForm} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>Clear</Button>
                            </Stack>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>Requested Medications</Typography>
                          <TableContainer sx={{ maxHeight: 300, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Table size="small" stickyHeader>
                              <TableHead><TableRow>
                                {['Date', 'Drug', 'Dose', 'Freq', 'Days', 'Qty', 'Route', 'Cost', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.7rem' }}>{h}</TableCell>)}
                              </TableRow></TableHead>
                              <TableBody>
                                {requestedMeds.slice(reqMedPage * 8, reqMedPage * 8 + 8).map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell sx={{ fontSize: '0.7rem' }}>{r.date}</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem' }}>{r.drug}</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem' }}>{r.dose}</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem' }}>{r.freq}</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem' }}>{r.days}</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem' }}>{r.qty}</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem' }}>{r.route}</TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem' }}>{r.cost}</TableCell>
                                    <TableCell>
                                      {r.date?.substring(0, 10) === actionDate ? (
                                        <IconButton size="small" color="error" onClick={() => handleDeleteMed(r)}><Delete fontSize="small" /></IconButton>
                                      ) : <IconButton size="small" disabled><LockIcon fontSize="small" /></IconButton>}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      </Grid>
                    )}

                    {/* ── OTHER SERVICES PANEL ── */}
                    {activeRequest === 'service' && (
                      <Grid container spacing={2} columns={12}>
                        <Grid item xs={5}>
                          <TextField fullWidth size="small" placeholder="Search services..." value={serviceSearchTerm} onChange={(e) => setServiceSearchTerm(e.target.value)} sx={{ mb: 1 }} />
                          <TableContainer sx={{ maxHeight: 220, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Table size="small" stickyHeader>
                              <TableHead><TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Service</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }} align="center">Request</TableCell>
                              </TableRow></TableHead>
                              <TableBody>
                                {services.filter(s => s.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()))
                                  .slice(servicePage * 8, servicePage * 8 + 8).map((srv, i) => (
                                  <TableRow key={i} hover>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{srv.name}</TableCell>
                                    <TableCell sx={{ fontSize: '0.75rem' }}>{formatWithOutSom(srv.amount)}</TableCell>
                                    <TableCell align="center"><IconButton size="small" color="primary" onClick={() => handleRequestService(srv)}><SendIcon fontSize="small" /></IconButton></TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {/* Manual Service */}
                          <Paper sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#fafafa', border: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>Manual Service</Typography>
                            <Stack spacing={1}>
                              <TextField size="small" fullWidth label="Service Name" value={manualServiceName} onChange={e => setManualServiceName(e.target.value)} />
                              <TextField size="small" fullWidth type="number" label="Amount" value={manualAmount} onChange={e => setManualAmount(e.target.value)} />
                              <Button size="small" variant="contained" onClick={handleManualService} sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>Request</Button>
                            </Stack>
                          </Paper>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>Requested Services</Typography>
                          <TableContainer sx={{ maxHeight: 300, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                            <Table size="small" stickyHeader>
                              <TableHead><TableRow>
                                {['Date', 'Service', 'Amount', 'By', 'Status', ''].map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem' }}>{h}</TableCell>)}
                              </TableRow></TableHead>
                              <TableBody>
                                {requestedServices.slice(reqServicePage * 8, reqServicePage * 8 + 8).map((r, i) => (
                                  <TableRow key={i}>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{r.date}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{r.service}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{formatWithOutSom(r.amount)}</TableCell>
                                    <TableCell sx={{ fontSize: '0.72rem' }}>{r.requestedBy}</TableCell>
                                    <TableCell><Chip size="small" label={r.status} color={r.status === 'paid' ? 'success' : 'warning'} sx={{ fontSize: '0.65rem' }} /></TableCell>
                                    <TableCell>
                                      {r.date?.substring(0, 10) === actionDate ? (
                                        <IconButton size="small" color="error" onClick={() => handleDeleteService(r)}><Close fontSize="small" /></IconButton>
                                      ) : <IconButton size="small" disabled><LockIcon fontSize="small" /></IconButton>}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      </Grid>
                    )}

                    {/* ── NURSE NOTE PANEL ── */}
                    {activeRequest === 'nurse' && (
                      <Grid container spacing={2}  columns={{ xs: 4, sm: 8, md: 12 }}>

                        {/* Left: Note History */}
                        <Grid item xs={4} size={6}>
                          <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
                            Note History
                          </Typography>
                          <Box sx={{ maxHeight: 380, overflowY: 'auto', pr: 1 }}>
                            {nurseNoteHistory.length === 0 ? (
                              <Box sx={{ textAlign: 'center', py: 4 }}>
                                <EditNoteIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  No nurse notes yet
                                </Typography>
                              </Box>
                          ) : [...nurseNoteHistory]
                                .sort((a, b) => {
                                  const dateA = new Date(`${a.dat} ${a.time || "00:00"}`);
                                  const dateB = new Date(`${b.dat} ${b.time || "00:00"}`);
                                  return dateB - dateA;
                                })
                                .map((note, i) => (
                                  <Paper
                                    key={i}
                                    elevation={0}
                                    onClick={() => handleSelectNurseNote(note)}
                                    sx={{
                                      p: 1.5,
                                      mb: 1.5,
                                      borderRadius: 2,
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      border: selectedNurseNote?.trans_code === note.trans_code
                                        ? '2px solid #00838f'
                                        : '1px solid #e0e0e0',
                                      borderLeft: selectedNurseNote?.trans_code === note.trans_code
                                        ? '4px solid #00838f'
                                        : note.dat?.substring(0,10) === actionDate
                                          ? '4px solid #00838f'
                                          : '4px solid #e0e0e0',
                                      bgcolor: selectedNurseNote?.trans_code === note.trans_code
                                        ? '#e0f7fa'
                                        : 'white',
                                      '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }
                                    }}
                                  >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Typography fontWeight={700} fontSize="0.8rem" color="#00838f">
                                    {note.dat}
                                  </Typography>
                                  <Chip
                                    label={note.time || ''}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#00838f', color: 'white' }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Box sx={{
                                    width: 24, height: 24, borderRadius: '50%', bgcolor: '#00838f',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'white', fontSize: '0.65rem', fontWeight: 700
                                  }}>
                                    {note.user?.charAt(0) || 'N'}
                                  </Box>
                                  <Typography fontSize="0.78rem" fontWeight={500}>{note.user}</Typography>
                                </Box>
                                <Typography
                                  fontSize="0.75rem" color="text.secondary"
                                  sx={{
                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical'
                                  }}
                                >
                                  {note.note}
                                </Typography>
                              </Paper>
                            ))}
                          </Box>
                        </Grid>

                        {/* Right: New / Edit Note */}
                        <Grid item xs={8} size={6}>
                          <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px dashed #b2dfdb' }}>
                            <Typography fontWeight={700} fontSize="1rem" sx={{ mb: 2 }}>
                              {selectedNurseNote
                                ? (selectedNurseNote.user === currentUser ? 'Edit Your Note' : 'View Note (Read Only)')
                                : 'New Nurse Note'}
                            </Typography>

                            {selectedNurseNote && (
                              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                <Chip
                                  label={`By: ${selectedNurseNote.user}`}
                                  size="small"
                                  sx={{ bgcolor: '#00838f', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                  label={`${selectedNurseNote.dat} ${selectedNurseNote.time}`}
                                  size="small"
                                  color="info"
                                />
                              </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                              <Button
                              variant="contained"
                              size="small"
                              onClick={handleAIGenerateNurseNote}
                              disabled={aiGeneratingNurseNote || nurseGenBlocked}   // ← add nurseGenBlocked
                              startIcon={
                                aiGeneratingNurseNote
                                  ? <CircularProgress size={16} color="inherit" />
                                  : nurseGenBlocked
                                    ? <LockIcon />
                                    : <AutoAwesomeIcon />
                              }
                              sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: 2,
                                background: nurseGenBlocked
                                  ? '#e0e0e0'
                                  : 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)'
                              }}
                            >
                              {aiGeneratingNurseNote
                                ? 'Generating...'
                                : nurseGenBlocked
                                  ? 'Note Already Generated'
                                  : 'Generate Nurse Note with AI'
                              }
                            </Button>
                            </Box>

                            <TextField
                              fullWidth multiline rows={10}
                              value={nurseNoteText}
                              onChange={(e) => setNurseNoteText(e.target.value)}
                              placeholder="Enter nurse note here... Document patient observations, vital signs, medications administered, treatments provided, and any significant changes in patient condition."
                              disabled={selectedNurseNote && selectedNurseNote.user !== currentUser}
                              sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                                '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#000' }
                              }}
                            />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                              <Button
                                variant="outlined" color="error" size="small"
                                startIcon={<Close />}
                                onClick={() => { setNurseNoteText(''); setSelectedNurseNote(null); }}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Clear
                              </Button>
                              <Button
                                variant="contained" size="small"
                                onClick={handleSaveNurseNote}
                                disabled={!nurseNoteText.trim() || (selectedNurseNote && selectedNurseNote.user !== currentUser)}
                                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                              >
                                {selectedNurseNote && selectedNurseNote.user === currentUser ? 'Update Note' : 'Save Note'}
                              </Button>
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                              <strong>Note:</strong> Each nurse can have only ONE note per day for each patient.
                              Saving a new note today will update your existing note for today if one exists.
                              You cannot edit notes written by other nurses.
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    )}

                    {/* ── VITALS RECORD PANEL ── */}
                    {activeRequest === 'vitals' && (
                      <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }}>

                        {/* Left: Vitals History */}
                        <Grid item xs={4} size={3}>
                          <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
                            Vitals History
                          </Typography>
                          <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 1 }}>
                            {vitalsHistory.length === 0 ? (
                              <Box sx={{ textAlign: 'center', py: 4 }}>
                                <MonitorHeartIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  No vitals recorded yet
                                </Typography>
                              </Box>
                            ) : vitalsHistory.map((v, i) => (
                              <Paper
                                key={i} elevation={0}
                                onClick={() => handleSelectVitalRecord(v)}
                                sx={{
                                  p: 1.5, mb: 1.5, borderRadius: 2, cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  border: selectedVitalRecord?.trans_code === v.trans_code
                                    ? '2px solid #c62828' : '1px solid #e0e0e0',
                                  borderLeft: selectedVitalRecord?.trans_code === v.trans_code
                                    ? '4px solid #c62828'
                                    : v.dat_vist?.substring(0,10) === actionDate
                                      ? '4px solid #c62828' : '4px solid #e0e0e0',
                                  bgcolor: selectedVitalRecord?.trans_code === v.trans_code
                                    ? '#ffebee' : 'white',
                                  '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Typography fontWeight={700} fontSize="0.8rem" color="#c62828">
                                    {v.dat_vist}
                                  </Typography>
                                  <Chip
                                    label={v.time || ''}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#c62828', color: 'white' }}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Box sx={{
                                    width: 24, height: 24, borderRadius: '50%', bgcolor: '#c62828',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: 'white', fontSize: '0.65rem', fontWeight: 700
                                  }}>
                                    {v.user?.charAt(0) || 'R'}
                                  </Box>
                                  <Typography fontSize="0.78rem" fontWeight={500}>{v.user}</Typography>
                                </Box>
                                <Typography fontSize="0.72rem" color="text.secondary">
                                  Temp: {v.tem}°C | Pulse: {v.pulse} bpm
                                </Typography>
                                <Typography fontSize="0.72rem" color="text.secondary">
                                  RR: {v.res}/min | BP: {v.pre}
                                </Typography>
                                <Typography fontSize="0.72rem" color="text.secondary">
                                  SpO₂: {v.spo2}% | Weight: {v.we} kg
                                </Typography>
                              </Paper>
                            ))}
                          </Box>
                        </Grid>

                        {/* Right: New / Edit Vitals Form */}
                        <Grid item xs={8} size={9}>
                          <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px dashed #ffcdd2' }}>
                            <Typography fontWeight={700} fontSize="1rem" sx={{ mb: 1 }}>
                              {selectedVitalRecord
                                ? (selectedVitalRecord.user === currentUser ? '✏️ Edit Vitals' : '👁️ View Vitals (Read Only)')
                                : '📋 New Vitals Record'}
                            </Typography>

                            {selectedVitalRecord && (
                              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                <Chip
                                  label={`By: ${selectedVitalRecord.user}`}
                                  size="small"
                                  sx={{ bgcolor: '#c62828', color: 'white', fontWeight: 600 }}
                                />
                                <Chip
                                  label={`${selectedVitalRecord.dat_vist} ${selectedVitalRecord.time}`}
                                  size="small" color="info"
                                />
                                {selectedVitalRecord.user === currentUser && (
                                  <Chip label="You can edit" size="small" color="success" sx={{ fontWeight: 600 }} />
                                )}
                              </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                            <Button
                            variant="contained"
                            size="small"
                            onClick={handleAIGenerateVitals}
                            disabled={aiGeneratingVitals || vitalsGenBlocked}
                            startIcon={
                              aiGeneratingVitals
                                ? <CircularProgress size={16} color="inherit" />
                                : vitalsGenBlocked
                                  ? <LockIcon />
                                  : <AutoAwesomeIcon />
                            }
                            sx={{
                              textTransform: 'none', fontWeight: 700, borderRadius: 2,
                              background: vitalsGenBlocked
                                ? '#e0e0e0'
                                : 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)'
                            }}
                          >
                            {aiGeneratingVitals
                              ? 'Generating...'
                              : vitalsGenBlocked
                                ? 'Vitals Already Generated'
                                : 'Generate Vitals with AI'
                            }
                          </Button>
                          </Box>

                            <Grid container spacing={2} columns={12}>
                              <Grid item xs={4}>
                                <TextField fullWidth size="small" type="number"
                                  label="Temperature (°C) *"
                                  value={temperature}
                                  onChange={(e) => setTemperature(e.target.value)}
                                  helperText="Required. Normal: 36.5-37.5°C"
                                  disabled={selectedVitalRecord && selectedVitalRecord.user !== currentUser}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField fullWidth size="small" type="number"
                                  label="Pulse (bpm)"
                                  value={pulse}
                                  onChange={(e) => setPulse(e.target.value)}
                                  helperText="Optional. Normal: 60-100 bpm"
                                  disabled={selectedVitalRecord && selectedVitalRecord.user !== currentUser}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField fullWidth size="small" type="number"
                                  label="Respiratory Rate (/min)"
                                  value={respiratoryRate}
                                  onChange={(e) => setRespiratoryRate(e.target.value)}
                                  helperText="Optional. Normal: 12-20/min"
                                  disabled={selectedVitalRecord && selectedVitalRecord.user !== currentUser}
                                />
                              </Grid>
                              <Grid item xs={4}>
                               <TextField
                                fullWidth
                                size="small"
                                label={isChild ? "Blood Pressure (Not required for child)" : "Blood Pressure (mmHg)"}
                                value={bloodPressure}
                                onChange={(e) => setBloodPressure(e.target.value)}
                                helperText={isChild ? "BP not checked for age 12 and below" : "Format: systolic/diastolic e.g. 120/80"}
                                disabled={isChild || (selectedVitalRecord && selectedVitalRecord.user !== currentUser)}
                              />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField fullWidth size="small" type="number"
                                  label="SpO₂ (%)"
                                  value={spO2}
                                  onChange={(e) => setSpO2(e.target.value)}
                                  helperText="Optional. Normal: 95-100%"
                                  disabled={selectedVitalRecord && selectedVitalRecord.user !== currentUser}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField fullWidth size="small" type="number"
                                  label="Weight (kg)"
                                  value={weight}
                                  onChange={(e) => setWeight(e.target.value)}
                                  helperText="Optional"
                                  disabled={selectedVitalRecord && selectedVitalRecord.user !== currentUser}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField fullWidth size="small" type="number"
                                  label="Height (cm)"
                                  value={height}
                                  onChange={(e) => setHeight(e.target.value)}
                                  helperText="Optional - for BMI"
                                  disabled={selectedVitalRecord && selectedVitalRecord.user !== currentUser}
                                />
                              </Grid>
                              <Grid item xs={4}>
                                <TextField fullWidth size="small"
                                  label="BMI" value={bmi} disabled
                                  helperText="Auto-calculated"
                                  sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#000', fontWeight: 700 } }}
                                />
                              </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
                              <Button
                                variant="outlined" color="error" size="small"
                                startIcon={<Close />}
                                onClick={clearVitalsForm}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Clear
                              </Button>
                              <Button
                                variant="contained" size="small"
                                onClick={handleSaveVitals}
                                disabled={!temperature || (selectedVitalRecord && selectedVitalRecord.user !== currentUser)}
                                sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                              >
                                {selectedVitalRecord ? 'Update Vitals' : 'Save Vitals'}
                              </Button>
                            </Box>

                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                              <strong>Note:</strong> Multiple vital sign records can be saved per day.
                              Record vitals during rounds, before procedures, or whenever monitoring is required.
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    )}

                    {/* ── FBS / RBS PANEL ── */}
                    {activeRequest === 'bloodsugar' && (
                      <Box>

                        {/* Sub-tabs: FBS / RBS */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <Button
                            size="small"
                            variant={bloodSugarTab === 'fbs' ? 'contained' : 'outlined'}
                            onClick={() => setBloodSugarTab('fbs')}
                            sx={{
                              textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3,
                              ...(bloodSugarTab === 'fbs'
                                ? { bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }
                                : { borderColor: '#e0e0e0', color: '#555' })
                            }}
                          >
                            FASTING BLOOD SUGAR (FBS)
                          </Button>
                          <Button
                            size="small"
                            variant={bloodSugarTab === 'rbs' ? 'contained' : 'outlined'}
                            onClick={() => setBloodSugarTab('rbs')}
                            sx={{
                              textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3,
                              ...(bloodSugarTab === 'rbs'
                                ? { bgcolor: '#e65100', '&:hover': { bgcolor: '#bf360c' } }
                                : { borderColor: '#e0e0e0', color: '#555' })
                            }}
                          >
                            RANDOM BLOOD SUGAR (RBS)
                          </Button>
                        </Box>

                        {/* ── FBS Sub-Tab ── */}
                        {bloodSugarTab === 'fbs' && (
                          <Grid container spacing={2} columns={12}>

                            {/* Left: FBS History */}
                            <Grid item xs={4}>
                              <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
                                FBS History
                              </Typography>
                              <Box sx={{ maxHeight: 350, overflowY: 'auto', pr: 1 }}>
                                {fbsHistory.length === 0 ? (
                                  <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <ScienceIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                      No FBS records yet
                                    </Typography>
                                  </Box>
                                ) : fbsHistory.map((f, i) => {
                                  const flag = getFBSFlag(f.FBS);
                                  return (
                                    <Paper
                                      key={i} elevation={0}
                                      onClick={() => handleSelectFbsRecord(f)}
                                      sx={{
                                        p: 1.5, mb: 1.5, borderRadius: 2, cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        border: selectedFbsRecord?.trans_code === f.trans_code
                                          ? '2px solid #c62828' : '1px solid #e0e0e0',
                                        borderLeft: `4px solid ${
                                          selectedFbsRecord?.trans_code === f.trans_code
                                            ? '#c62828' : flag?.color || '#e0e0e0'
                                        }`,
                                        bgcolor: selectedFbsRecord?.trans_code === f.trans_code
                                          ? '#ffebee' : 'white',
                                        '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography fontWeight={700} fontSize="0.8rem">{f.dat}</Typography>
                                        <Chip
                                          label={`${f.FBS} mmol/L`}
                                          size="small"
                                          sx={{ fontWeight: 700, bgcolor: flag?.color || '#555', color: 'white', fontSize: '0.7rem' }}
                                        />
                                      </Box>
                                      {flag && (
                                        <Chip
                                          label={flag.label} size="small" variant="outlined"
                                          sx={{ fontSize: '0.65rem', fontWeight: 600, borderColor: flag.color, color: flag.color, mb: 0.5 }}
                                        />
                                      )}
                                      {f.remark && (
                                        <Typography fontSize="0.72rem" color="text.secondary">{f.remark}</Typography>
                                      )}
                                      <Typography fontSize="0.68rem" color="text.secondary">By: {f.user}</Typography>
                                    </Paper>
                                  );
                                })}
                              </Box>
                            </Grid>

                            {/* Right: FBS Entry Form */}
                            <Grid item xs={8}>
                              <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px dashed #ffcdd2' }}>
                                <Typography fontWeight={700} fontSize="1rem" sx={{ mb: 2 }}>
                                  {selectedFbsRecord ? '✏️ Edit FBS Record' : 'New FBS Record'}
                                </Typography>

                                {selectedFbsRecord && (
                                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                    <Chip
                                      label={`By: ${selectedFbsRecord.user}`}
                                      size="small"
                                      sx={{ bgcolor: '#c62828', color: 'white', fontWeight: 600 }}
                                    />
                                    <Chip
                                      label={`${selectedFbsRecord.dat} ${selectedFbsRecord.time || ''}`}
                                      size="small" color="info"
                                    />
                                  </Box>
                                )}

                                <Grid container spacing={2} columns={12}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth size="small" type="number"
                                      label="Fasting Blood Sugar (FBS) *"
                                      value={fbsValue}
                                      onChange={(e) => setFbsValue(e.target.value)}
                                      InputProps={{
                                        endAdornment: <Typography color="text.secondary" fontSize="0.8rem">mmol/L</Typography>
                                      }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      Normal: 3.9-5.6 | Pre-diabetic: 5.6-6.9 mmol/L
                                    </Typography>
                                    {fbsValue && getFBSFlag(fbsValue) && (
                                      <Chip
                                        label={getFBSFlag(fbsValue).label}
                                        size="small"
                                        sx={{ mt: 1, fontWeight: 700, bgcolor: getFBSFlag(fbsValue).color, color: 'white' }}
                                      />
                                    )}
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth size="small" multiline rows={3}
                                      label="Remarks / Notes"
                                      value={fbsRemarks}
                                      onChange={(e) => setFbsRemarks(e.target.value)}
                                      placeholder="Optional"
                                    />
                                  </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
                                  <Button
                                    variant="outlined" color="error" size="small"
                                    startIcon={<Close />}
                                    onClick={() => { setFbsValue(''); setFbsRemarks(''); setSelectedFbsRecord(null); }}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                  >
                                    Clear
                                  </Button>
                                  <Button
                                    variant="contained" size="small"
                                    onClick={handleSaveFBS}
                                    disabled={!fbsValue}
                                    sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                                  >
                                    {selectedFbsRecord ? 'Update FBS' : 'Save FBS'}
                                  </Button>
                                </Box>

                                <Paper sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#fff8e1' }}>
                                  <Typography variant="caption" fontWeight={700} color="#bf360c">
                                    Reference Ranges (mmol/L):
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                    • Normal: 3.9-5.6 | • Pre-diabetic: 5.6-6.9 | • Diabetic: ≥ 7.0 | • Low: {'<'} 3.9
                                  </Typography>
                                </Paper>
                              </Paper>
                            </Grid>
                          </Grid>
                        )}

                        {/* ── RBS Sub-Tab ── */}
                        {bloodSugarTab === 'rbs' && (
                          <Grid container spacing={2} columns={12}>

                            {/* Left: RBS History */}
                            <Grid item xs={4}>
                              <Typography variant="caption" fontWeight={700} sx={{ mb: 1, display: 'block' }}>
                                RBS History
                              </Typography>
                              <Box sx={{ maxHeight: 350, overflowY: 'auto', pr: 1 }}>
                                {rbsHistory.length === 0 ? (
                                  <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <ScienceIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                      No RBS records yet
                                    </Typography>
                                  </Box>
                                ) : rbsHistory.map((r, i) => {
                                  const flag = getRBSFlag(r.RBS);
                                  return (
                                    <Paper
                                      key={i} elevation={0}
                                      onClick={() => handleSelectRbsRecord(r)}
                                      sx={{
                                        p: 1.5, mb: 1.5, borderRadius: 2, cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        border: selectedRbsRecord?.trans_code === r.trans_code
                                          ? '2px solid #e65100' : '1px solid #e0e0e0',
                                        borderLeft: `4px solid ${
                                          selectedRbsRecord?.trans_code === r.trans_code
                                            ? '#e65100' : flag?.color || '#e0e0e0'
                                        }`,
                                        bgcolor: selectedRbsRecord?.trans_code === r.trans_code
                                          ? '#fff3e0' : 'white',
                                        '&:hover': { boxShadow: 2, transform: 'translateY(-1px)' }
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography fontWeight={700} fontSize="0.8rem">{r.dat}</Typography>
                                        <Chip
                                          label={`${r.RBS} mmol/L`}
                                          size="small"
                                          sx={{ fontWeight: 700, bgcolor: flag?.color || '#555', color: 'white', fontSize: '0.7rem' }}
                                        />
                                      </Box>
                                      {flag && (
                                        <Chip
                                          label={flag.label} size="small" variant="outlined"
                                          sx={{ fontSize: '0.65rem', fontWeight: 600, borderColor: flag.color, color: flag.color, mb: 0.5 }}
                                        />
                                      )}
                                      {r.remark && (
                                        <Typography fontSize="0.72rem" color="text.secondary">{r.remark}</Typography>
                                      )}
                                      <Typography fontSize="0.68rem" color="text.secondary">By: {r.user}</Typography>
                                    </Paper>
                                  );
                                })}
                              </Box>
                            </Grid>

                            {/* Right: RBS Entry Form */}
                            <Grid item xs={8}>
                              <Paper sx={{ p: 2.5, borderRadius: 2, border: '1px dashed #ffe0b2' }}>
                                <Typography fontWeight={700} fontSize="1rem" sx={{ mb: 2 }}>
                                  {selectedRbsRecord ? '✏️ Edit RBS Record' : 'New RBS Record'}
                                </Typography>

                                {selectedRbsRecord && (
                                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                                    <Chip
                                      label={`By: ${selectedRbsRecord.user}`}
                                      size="small"
                                      sx={{ bgcolor: '#e65100', color: 'white', fontWeight: 600 }}
                                    />
                                    <Chip
                                      label={`${selectedRbsRecord.dat} ${selectedRbsRecord.time || ''}`}
                                      size="small" color="info"
                                    />
                                  </Box>
                                )}

                                <Grid container spacing={2} columns={12}>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth size="small" type="number"
                                      label="Random Blood Sugar (RBS) *"
                                      value={rbsValue}
                                      onChange={(e) => setRbsValue(e.target.value)}
                                      InputProps={{
                                        endAdornment: <Typography color="text.secondary" fontSize="0.8rem">mmol/L</Typography>
                                      }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      Normal: 3.9-7.8 | Pre-diabetic: 7.8-11.0 mmol/L
                                    </Typography>
                                    {rbsValue && getRBSFlag(rbsValue) && (
                                      <Chip
                                        label={getRBSFlag(rbsValue).label}
                                        size="small"
                                        sx={{ mt: 1, fontWeight: 700, bgcolor: getRBSFlag(rbsValue).color, color: 'white' }}
                                      />
                                    )}
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      fullWidth size="small" multiline rows={3}
                                      label="Remarks / Notes"
                                      value={rbsRemarks}
                                      onChange={(e) => setRbsRemarks(e.target.value)}
                                      placeholder="Optional"
                                    />
                                  </Grid>
                                </Grid>

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}>
                                  <Button
                                    variant="outlined" color="error" size="small"
                                    startIcon={<Close />}
                                    onClick={() => { setRbsValue(''); setRbsRemarks(''); setSelectedRbsRecord(null); }}
                                    sx={{ textTransform: 'none', fontWeight: 600 }}
                                  >
                                    Clear
                                  </Button>
                                  <Button
                                    variant="contained" size="small"
                                    onClick={handleSaveRBS}
                                    disabled={!rbsValue}
                                    sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
                                  >
                                    {selectedRbsRecord ? 'Update RBS' : 'Save RBS'}
                                  </Button>
                                </Box>

                                <Paper sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: '#fff8e1' }}>
                                  <Typography variant="caption" fontWeight={700} color="#bf360c">
                                    Reference Ranges (mmol/L):
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                    • Normal: 3.9-7.8 | • Pre-diabetic: 7.8-11.0 | • Diabetic: ≥ 11.1 | • Low: {'<'} 3.9
                                  </Typography>
                                </Paper>
                              </Paper>
                            </Grid>
                          </Grid>
                        )}

                      </Box>
                    )}

                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: "#f5f7fa", borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={onClose} variant="contained" color="error" sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, px: 4 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ NEW ENTRY DIALOG ═══ */}
      <Dialog open={dialogOpen.open} fullWidth maxWidth="md" TransitionComponent={Zoom}>
        <DialogTitle sx={{ background: "linear-gradient(135deg, #14aa41 0%, #1ac971 100%)", color: "white", fontWeight: 700 }}>
          Add {dialogOpen.field} — {dateOptions.find(d => d.value === actionDate)?.label || actionDate}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2} columns={12}>
            <Grid item xs={3}>
              <Typography fontWeight={600} sx={{ mb: 1 }}>Round State</Typography>
              <Autocomplete options={["Morning Round", "Afternoon Round", "Evening Round", "Review Round"]} value={roundState} onChange={(e, v) => setRoundState(v)}
                renderInput={(p) => <TextField {...p} label="Round State" />} />
            </Grid>
            <Grid item xs={3}>
              <Typography fontWeight={600} sx={{ mb: 1 }}>Doctor</Typography>
              <TextField value={currentUser} disabled fullWidth sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f5f5f5' } }} />
            </Grid>
            <Grid item xs={3}>
              <Typography fontWeight={600} sx={{ mb: 1 }}>Date</Typography>
              <Paper sx={{
                p: 1.5, borderRadius: 1.5, bgcolor: '#fff3e0',
                border: '2px solid #e65100', display: 'flex',
                alignItems: 'center', gap: 1, height: 40
              }}>
                <CalendarTodayIcon sx={{ color: '#e65100', fontSize: 18 }} />
                <Typography fontWeight={700} color="#e65100" fontSize="0.95rem">
                  {dateOptions.find(d => d.value === actionDate)?.label || actionDate}
                </Typography>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Controlled by Action Date selector
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography fontWeight={600} sx={{ mb: 1 }}>Time</Typography>
              <TextField type="time" value={roundTime} onChange={(e) => setRoundTime(e.target.value)} fullWidth />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography fontWeight={600} sx={{ mb: 1 }}>Notes</Typography>
            <Autocomplete freeSolo disableClearable options={suggestionsData[dialogOpen.field] || []}
              filterOptions={(opts, { inputValue }) => { const parts = inputValue.split(/[.,]/); const last = parts[parts.length - 1].trim().toLowerCase(); return opts.filter(o => o.toLowerCase().includes(last)); }}
              inputValue={dialogText}
              onInputChange={(e, v, reason) => { if (reason === "input") setDialogText(v); }}
              onChange={(e, v, reason) => { if (v && reason === "selectOption") { const parts = dialogText.split(/[.,]/); parts[parts.length - 1] = ` ${v}`; setDialogText(parts.join(", ")); } }}
              renderInput={(params) => <TextField {...params} fullWidth multiline rows={8} placeholder={`Enter ${dialogOpen.field}...`}
                inputProps={{ ...params.inputProps, spellCheck: true, autoCapitalize: "sentences" }} />}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleDialogClose} sx={{ textTransform: "none", fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogSave}
            sx={{ textTransform: "none", fontWeight: 600, px: 3, background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)" }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ HISTORY DIALOGS ═══ */}
      <VitalsHistoryDialogAdmission open={vitalsHistoryOpen} onClose={() => setVitalsHistoryOpen(false)} patientData={patient} startDate={startDate} />
      <LabHistoryDialogAdmission open={labHistoryOpen} onClose={() => setLabHistoryOpen(false)} patientData={patient} sendstartDate={startDate} />
      <ScanHistoryDialogAdmission open={scanHistoryOpen} onClose={() => setScanHistoryOpen(false)} patientData={patient} sendstartDate={startDate} />
      <MedicalHistoryDialogAdmission open={medHistoryOpen} onClose={() => setMedHistoryOpen(false)} id={patient?.id || ''} sendstartDate={startDate} />
    </>
  );
};

export default OutPatientWardRoundsDialog;